import { z } from "zod";
import {
  drawingEntitySchema,
  entityColorSchema,
  hatchGradientSchema,
  hatchPatternLineSchema,
} from "@dxf-ai-editor/drawing-model";

// A10A — compatibility-barrel seam. The shared session/space/entity-preview
// primitives, the telemetry/audit-ring event schemas, and the style-table +
// undo/redo-history schemas that used to live inline here were extracted
// into sibling modules (`editorPrimitives.ts`, `editorTelemetry.ts`,
// `editorStyleAndHistory.ts`) to shrink this red file. This is a
// behaviour-preserving MOVE, not a redesign: every symbol is imported (for
// local use in `editorShellStateSchema` / `editorCommandSchema` below) AND
// re-exported (so `./editor` / `@dxf-ai-editor/contracts` keep the EXACT
// same export surface and every moved symbol keeps its original type
// identity — a re-export, never a redeclaration).
import {
  FLOAT_EPSILON,
  bootstrapAbsoluteAdminEmail,
  userRoleSchema,
  accessStatusSchema,
  commandSourceSchema,
  matchpropModeSchema,
  panelSchema,
  layoutContextSchema,
  spaceKindSchema,
  entityTypeSchema,
  sessionUserSchema,
  viewportSnapshotSchema,
  drawingSpaceSchema,
  entityPreviewSchema,
  propertyValueMapSchema,
  transcriptEntrySchema,
} from "./editorPrimitives";
export {
  FLOAT_EPSILON,
  bootstrapAbsoluteAdminEmail,
  userRoleSchema,
  accessStatusSchema,
  commandSourceSchema,
  matchpropModeSchema,
  panelSchema,
  layoutContextSchema,
  spaceKindSchema,
  entityTypeSchema,
  sessionUserSchema,
  viewportSnapshotSchema,
  drawingSpaceSchema,
  entityPreviewSchema,
  propertyValueMapSchema,
  transcriptEntrySchema,
};

import {
  telemetryParamsSchema,
  auditOperationParamsSchema,
  telemetryToneSchema,
  auditEventSchema,
  TELEMETRY_SCHEMA_VERSION,
  telemetryEventKindSchema,
  telemetryNavKindSchema,
  telemetryStatusSchema,
  telemetryCommandEventSchema,
  telemetryViewOpEventSchema,
  telemetryUiClickEventSchema,
  telemetryUiToggleEventSchema,
  telemetryUiNavEventSchema,
  telemetryEventSchema,
  telemetryBatchSchema,
} from "./editorTelemetry";
export {
  telemetryParamsSchema,
  auditOperationParamsSchema,
  telemetryToneSchema,
  auditEventSchema,
  TELEMETRY_SCHEMA_VERSION,
  telemetryEventKindSchema,
  telemetryNavKindSchema,
  telemetryStatusSchema,
  telemetryCommandEventSchema,
  telemetryViewOpEventSchema,
  telemetryUiClickEventSchema,
  telemetryUiToggleEventSchema,
  telemetryUiNavEventSchema,
  telemetryEventSchema,
  telemetryBatchSchema,
};

import {
  textStyleEntrySchema,
  dimensionStyleEntrySchema,
  multileaderStyleEntrySchema,
  styleTablesShellSchema,
  historyEntrySchema,
  historyBatchSchema,
} from "./editorStyleAndHistory";
export {
  textStyleEntrySchema,
  dimensionStyleEntrySchema,
  multileaderStyleEntrySchema,
  styleTablesShellSchema,
  historyEntrySchema,
  historyBatchSchema,
};
export type { HistoryEntry, HistoryBatch } from "./editorStyleAndHistory";

export const editorShellStateSchema = z.object({
  version: z.literal(1),
  session: z.object({
    currentUserEmail: z.string().email(),
    bootstrapAbsoluteAdminEmail: z.string().email(),
    users: z.array(sessionUserSchema).min(1),
  }),
  drawing: z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    spaces: z.array(drawingSpaceSchema).min(1),
    activeSpaceId: z.string().min(1),
    layoutContext: layoutContextSchema,
    entities: z.array(entityPreviewSchema),
    styleTables: styleTablesShellSchema.default({}),
  }),
  ui: z.object({
    activePanel: panelSchema,
  }),
  propertyWorkflows: z.object({
    defaultObjectProperties: propertyValueMapSchema,
    matchpropSourceEntityId: z.string().min(1).nullable(),
  }),
  selection: z.object({
    entityIds: z.array(z.string().min(1)),
    /**
     * Q25.C — AutoCAD `SELECT Previous` pickset.
     *
     * Every successful `shell/select-entities` snapshots the *prior*
     * `entityIds` into `previousPickset` before overwriting. Undo /
     * redo (geometry mutation) clears `entityIds` (selection drops on
     * structural change) but **preserves `previousPickset`**, so the
     * user can recover the prior pick via `selection/restore-previous`
     * (the reducer-level analogue of AutoCAD's `SELECT P` option).
     *
     * Optional + defaulted to keep persisted state from before the
     * PR-C migration parseable; `editorShellStateSchema.parse` of an
     * older snapshot fills in `[]` automatically.
     */
    previousPickset: z.array(z.string().min(1)).default([]),
  }),
  commandLine: z.object({
    draft: z.string(),
    lastSubmitted: z.string().nullable(),
    transcript: z.array(transcriptEntrySchema).max(48),
  }),
  audit: z.object({
    nextSequence: z.number().int().positive(),
    events: z.array(auditEventSchema).max(48),
    /**
     * CC-1e P2-1 — SEPARATE, independently-bounded queue for the NON-command
     * interaction telemetry family (ui-click / ui-toggle / ui-nav, the ТЗ §14
     * "clicks" axis). These display-hidden events MUST NOT share the visible
     * `events` ring: routing them there let a burst of ui/* telemetry (rapid
     * ribbon-tab switching, ORTHO/OSNAP toggling) evict real command / view-op
     * entries out of the cap-bounded ring, silently losing recent History-panel
     * data. Keeping them here preserves the visible ring exactly as pre-CC-1e
     * (the History panel reads `events` only) while the CC-1a transport observes
     * BOTH queues, so ui/* telemetry still reaches the server channel. Optional
     * + defaulted-empty so snapshots persisted before this slice parse cleanly.
     */
    uiEvents: z.array(auditEventSchema).max(48).default([]),
  }),
  /**
   * Phase 4.1.a — Undo / Redo history slice. Optional + defaulted so
   * persisted snapshots from before Phase 4.1 parse cleanly (the
   * legacy snapshots fill in empty stacks).
   *
   * `past` — committed entries, newest at end. Ctrl-Z pops from here.
   * `future` — entries undone (most recent at end). Ctrl-Y / Ctrl-Shift-Z
   *   pops from here.
   * `current` — in-progress batch; populated between
   *   `history/begin-batch` and `history/commit-batch`. Nested batches
   *   flatten to the outermost.
   * `depthCap` — entries beyond this drop from the head of `past` to
   *   keep RAM bounded. Default 100 (UNDOLIMIT sysvar override later).
   *
   * 4.1.a ships the slice + commands; per-command inverse generation
   * lands in 4.1.c onward, so the slice will mostly stay empty until
   * mutation files start populating their inverse fields.
   */
  history: z
    .object({
      past: z.array(historyEntrySchema).default([]),
      future: z.array(historyEntrySchema).default([]),
      current: historyBatchSchema.nullable().default(null),
      depthCap: z.number().int().min(0).default(100),
      droppedCount: z.number().int().min(0).default(0),
    })
    .default({
      past: [],
      future: [],
      current: null,
      depthCap: 100,
      droppedCount: 0,
    }),
});

export const editorCommandSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("session/switch-user"),
    source: commandSourceSchema,
    userEmail: z.string().email(),
  }),
  z.object({
    type: z.literal("shell/open-panel"),
    source: commandSourceSchema,
    panel: panelSchema,
  }),
  z.object({
    type: z.literal("shell/activate-space"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    layoutContext: layoutContextSchema.optional(),
  }),
  z.object({
    // LAYOUT R / LAYOUT RENAME — AutoCAD parity. Renames a layout tab's
    // display name without changing its id. The Model space cannot be
    // renamed (AutoCAD parity); duplicate and empty names are rejected
    // by the reducer.
    type: z.literal("shell/rename-space"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    newName: z.string().min(1).max(255),
  }),
  z.object({
    // LAYOUT N / LAYOUT NEW — AutoCAD parity. Creates a new EMPTY
    // paper-space layout tab (kind="layout", zero entities) WITHOUT
    // activating it (AutoCAD's LAYOUT New stays on the current tab).
    // Only paper-space layouts can be created — the Model space is a
    // document singleton — so `kind` is pinned to the "layout" literal.
    // Duplicate names (case-insensitive, AutoCAD tab-name uniqueness)
    // and the 255-layout AutoCAD cap are rejected by the reducers.
    type: z.literal("shell/add-space"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    name: z.string().min(1).max(255),
    kind: z.literal("layout"),
  }),
  z.object({
    // LAYOUT D / LAYOUT DELETE — AutoCAD parity. Removes a paper-space
    // layout tab and all of its entities. The Model tab cannot be
    // deleted and the LAST remaining layout cannot be deleted (AutoCAD
    // keeps >= 1 layout); both are rejected by the reducers. Deleting
    // the ACTIVE layout activates the adjacent remaining layout tab.
    type: z.literal("shell/remove-space"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
  }),
  z.object({
    type: z.literal("shell/set-layout-context"),
    source: commandSourceSchema,
    layoutContext: z.enum(["paper", "viewport"]),
  }),
  z.object({
    type: z.literal("shell/select-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("shell/clear-selection"),
    source: commandSourceSchema,
  }),
  z.object({
    /**
     * Q25.C — AutoCAD `SELECT Previous` reducer command.
     *
     * Copies `state.selection.previousPickset` back into
     * `state.selection.entityIds`. Both fields are then equal: the
     * "previous" pickset persists (unchanged) so subsequent SELECT P
     * invocations remain idempotent. No CLI parser is wired in PR-C —
     * the command is reducer-only; PR-G adds the SELECT P UI hook.
     */
    type: z.literal("selection/restore-previous"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("shell/delete-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  /**
   * Phase 4.1.c.2 — single-entity delete for undo/redo inverse dispatch.
   * Mirrors `shell/delete-entities` but targets exactly one entity by id.
   * Used as the inverse command emitted by `computeInverse` for every
   * `geometry/create-*` command. Also used as the forward command for
   * `geometry/delete-entity` → `geometry/restore-entity` pairs in later
   * phases. Routes through `reduceGeometryDocumentWithMutations` via the
   * `shell/delete-entities` code path with a single-element array.
   */
  z.object({
    type: z.literal("geometry/delete-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
  }),
  /**
   * Phase 4.1.c.2 follow-up (P1-5) — full-entity restore for undo of delete.
   *
   * Carries the complete `DrawingEntity` snapshot (as `z.unknown()` to avoid
   * importing `@dxf-ai-editor/drawing-model` from contracts). The geometry
   * reducer re-inserts the entity with ALL its original fields preserved —
   * including `id`, `layer`, `color`, `lineweight`, `transparency`, etc.
   * This is the inverse of `geometry/delete-entity`.
   */
  z.object({
    type: z.literal("geometry/restore-entity"),
    source: commandSourceSchema,
    /** Full DrawingEntity snapshot — all fields including id preserved. */
    entity: drawingEntitySchema,
    spaceId: z.string().min(1),
  }),
  /**
   * Phase 4.1.c.2 follow-up (P1-4) — full-entities restore for undo of
   * `shell/delete-entities`. Carries the complete DrawingEntity snapshot
   * array. The geometry reducer re-inserts all entities preserving every
   * field. This is the inverse of `shell/delete-entities`.
   */
  z.object({
    type: z.literal("shell/restore-entities"),
    source: commandSourceSchema,
    /** Array of full DrawingEntity snapshots — all fields including id preserved. */
    entities: z.array(drawingEntitySchema).min(1),
    spaceId: z.string().min(1),
  }),
  /**
   * PR #324 / EPIC-VIEWPORT-CLIP #318 R2 — in-place restore of a surviving
   * viewport's clip fields, the inverse half of a delete that cleared a
   * DANGLING viewport clip. When a boundary entity B1 is erased, the delete
   * reducer (`clearDanglingViewportClips`) clears the surviving viewport that
   * pointed at B1 — dropping `clippingBoundaryHandle`, the group-90 `0x10000`
   * non-rectangular-clip bit, and the legacy `extraGroups`-340 carrier. The
   * delete inverse re-adds B1; THIS command re-sets each surviving viewport
   * (matched by `viewport.id` within `spaceId`) to its full pre-delete
   * snapshot, restoring all three clip carriers verbatim (preserve-source).
   *
   * System-only (always emitted by `computeInverse`, never user-issued). It
   * REPLACES the existing viewport by id — it does NOT append. Each entry
   * carries the FULL DrawingEntity snapshot so no clip field is inferred.
   */
  z.object({
    type: z.literal("geometry/restore-viewport-clip"),
    source: commandSourceSchema,
    /**
     * Each entry re-sets the EXISTING viewport (matched by `viewport.id` within
     * `spaceId`) to this full pre-delete snapshot, restoring
     * `clippingBoundaryHandle` + the group-90 `0x10000` non-rect bit + the
     * legacy `extraGroups`-340 carrier. `min(1)` — computeInverse returns null
     * (never this command) when no clip dangled.
     */
    viewports: z
      .array(
        z.object({ spaceId: z.string().min(1), viewport: drawingEntitySchema }),
      )
      .min(1),
    /**
     * @codex #496 P2 — the VPCLIP set / set-polygon / remove forwards ALSO strip
     * the imported SPATIAL_FILTER OBJECTS chain
     * (ext-dict → ACAD_FILTER → SPATIAL_FILTER) out of the document's verbatim
     * `rawObjectGroups` via `detachInvalidatedViewportClipObjects` — a
     * document-scope mutation the per-viewport entity snapshot above cannot reach.
     * When the target viewport carried such a chain, `computeInverse` snapshots
     * the PRE-mutation `rawObjectGroups` here so undo re-attaches the removed
     * OBJECTS records verbatim (preserve-source); otherwise absent (a strip that
     * removed nothing needs no restore, and the delete-inverse callers that clear
     * a DANGLING clip never touch the OBJECTS stream). Optional; when present the
     * reducer replaces `document.rawObjectGroups` with it.
     */
    restoreRawObjectGroups: z
      .array(z.object({ code: z.number().int(), value: z.string() }))
      .optional(),
  }),
  /**
   * @codex PR #785 round 2, F2 — PRESENCE-BIT restore for a `properties/apply`
   * undo: an in-place, full-typed-snapshot REPLACE of an EXISTING entity,
   * matched by `entity.id` within `spaceId`.
   *
   * ## Why the value-replay inverse cannot express it
   *
   * The live `properties/apply` inverse is a DISPLAY-STRING replay: it
   * snapshots the prior value of every preview key the apply changed and
   * replays those strings forward through the typed writer. A string can carry
   * a VALUE; it cannot carry the difference between an optional that is ABSENT
   * and one that is PRESENT and equal to the default its absence implies. On
   * the DXF wire that difference is data — a heavy POLYLINE without group 40
   * INHERITS width 0 into every VERTEX that omits its own 40, so the group's
   * EXISTENCE is information and the writer is presence-preserving by design
   * (`dxf-io/writer.ts`: `if (g.defaultStartWidth !== undefined) …`). Editing
   * that width and undoing replayed the DISPLAYED inherited `"0"` and left an
   * EXPLICIT zero behind, so the next export emitted a group the source file
   * never had. Replaying the pre-edit TYPED SNAPSHOT restores the presence bits
   * verbatim, because it never re-derives a field from a string at all.
   *
   * System-only: emitted exclusively by the `editorStore` `properties/apply`
   * inverse composition (inside a `history/replay-inverses` batch), never
   * user-issued and never AI-callable. Like `geometry/restore-viewport-clip` it
   * REPLACES by id — it NEVER appends (appending would duplicate the id) — and
   * each replacement is emitted as an `updated` diff so the compact-cache sync
   * re-learns the restored state.
   *
   * Distinct from `geometry/restore-viewport-clip` on purpose: that command is
   * the viewport-clip delete inverse (it additionally carries the document-scope
   * `restoreRawObjectGroups` SPATIAL_FILTER re-attach and is audited under the
   * `viewport` category). Routing an arbitrary entity snapshot through a field
   * named `viewport` mis-files every such restore in the audit trail.
   */
  z.object({
    type: z.literal("geometry/restore-entity-snapshot"),
    source: commandSourceSchema,
    /**
     * Each entry re-sets the EXISTING entity (matched by `entity.id` within
     * `spaceId`) to this full pre-edit `DrawingEntity` snapshot, restoring every
     * field INCLUDING the presence/absence of each optional. `min(1)` — the
     * inverse composition only emits the command when it has a snapshot to
     * restore.
     */
    entities: z
      .array(
        z.object({ spaceId: z.string().min(1), entity: drawingEntitySchema }),
      )
      .min(1),
  }),
  z.object({
    type: z.literal("properties/apply"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)),
    values: propertyValueMapSchema,
    /**
     * Per-entity value overrides for undo/redo inverse dispatch (Phase 4.1.e).
     * Keys are entity IDs; values are property maps. When present for a given
     * entityId, the per-entity map is used INSTEAD of `values` for that entity.
     * Emitted by computeInverse when old property values are heterogeneous
     * across the target entities.
     */
    perEntityValues: z.record(z.string(), propertyValueMapSchema).optional(),
  }),
  z.object({
    type: z.literal("chprop/apply"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    property: z.string().min(1),
    value: z.string(),
  }),
  z.object({
    type: z.literal("matchprop/set-source"),
    source: commandSourceSchema,
    entityId: z.string().min(1).nullable(),
  }),
  z.object({
    type: z.literal("matchprop/apply"),
    source: commandSourceSchema,
    targetEntityIds: z.array(z.string().min(1)).min(1),
    mode: matchpropModeSchema,
  }),
  z.object({
    type: z.literal("geometry/create-line"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    start: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    end: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    // CECOLOR — the current default colour stamped onto the new entity at the
    // dispatch boundary (`useDraftSession` reads `getCeColor()`). Optional so a
    // command omitting it (legacy / tests) defaults to ByLayer in the reducer.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-point"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    location: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    // PDMODE-marker rotation about the UCS X axis — DXF group 50, DEGREES
    // (stored verbatim into the model + exported verbatim as group 50).
    // Default 0 mirrors AutoCAD: a freshly clicked POINT carries no rotation
    // unless the user explicitly types one (or PDMODE is non-zero and
    // the renderer uses it to orient the marker shape).
    rotation: z.number().default(0),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-circle"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    center: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    radius: z.number().positive(),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-arc"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    center: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    radius: z.number().positive(),
    startAngle: z.number(),
    endAngle: z.number(),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    // ELLIPSE — center-mode flow. The state machine collects a center
    // point, a major-axis endpoint (absolute world coords) and a
    // minor-axis distance. The reducer converts (majorAxisEnd - center)
    // into the DXF-relative major-axis vector, derives ratio = minor /
    // major, and stores startParam=0/endParam=2π for the full ellipse.
    // Partial ellipses are authored through the property panel by
    // editing start/end parameters after the entity exists.
    type: z.literal("geometry/create-ellipse"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    center: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    majorAxisEnd: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    minorDistance: z.number().positive(),
    startParam: z.number().default(0),
    endParam: z.number().default(Math.PI * 2),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  // SPLINE — full-fidelity NURBS authoring. Two construction modes:
  //   - Fit-points mode: user supplies points the curve passes through.
  //     Optional start/end tangent vectors clamp the derivatives.
  //   - Control-points mode: user supplies degree, control points,
  //     knot vector, and (optionally) per-CP weights. Curve approximates
  //     the control polygon (NURBS).
  // Per CLAUDE.md ПРИОРИТЕТ #1 — cubic Bezier rendering only, no
  // simplification. Defaults match AutoCAD's SPLINE command.
  z.object({
    type: z.literal("geometry/create-spline"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    degree: z.number().int().positive().default(3),
    closed: z.boolean().default(false),
    rational: z.boolean().default(false),
    fitPoints: z
      .array(
        z.object({
          x: z.number(),
          y: z.number(),
          z: z.number().default(0),
        }),
      )
      .default([]),
    controlPoints: z
      .array(
        z.object({
          x: z.number(),
          y: z.number(),
          z: z.number().default(0),
        }),
      )
      .default([]),
    knots: z.array(z.number()).default([]),
    weights: z.array(z.number()).default([]),
    startTangent: z
      .object({
        x: z.number(),
        y: z.number(),
        z: z.number().default(0),
      })
      .optional(),
    endTangent: z
      .object({
        x: z.number(),
        y: z.number(),
        z: z.number().default(0),
      })
      .optional(),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-lwpolyline"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    vertices: z
      .array(
        z.object({
          x: z.number(),
          y: z.number(),
          bulge: z.number().default(0),
          startWidth: z.number().default(0),
          endWidth: z.number().default(0),
        }),
      )
      .min(2),
    closed: z.boolean(),
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  // DONUT — closed LWPOLYLINE encoding of a filled annulus (donut).
  // AutoCAD models DONUT as a single closed LWPOLYLINE with two
  // bulge=1 vertices, where each vertex carries constant per-segment
  // width (group codes 40/41) equal to (outerDiameter - innerDiameter)
  // / 2. The two bulged segments draw two semicircles whose midline
  // radius is (innerDiameter + outerDiameter) / 4 — the painted band
  // therefore spans innerR..outerR exactly, matching the visual annulus.
  // innerDiameter == 0 collapses to a solid disk (still valid).
  z.object({
    type: z.literal("geometry/create-donut"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    center: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    innerDiameter: z.number().min(0),
    outerDiameter: z.number().positive(),
    // CECOLOR — current colour stamped at the dispatch boundary (DONUT creates a
    // closed LWPOLYLINE; AutoCAD assigns CECOLOR to it). Optional → ByLayer in
    // the reducer when absent (legacy / tests).
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-text"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    insertionPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    height: z.number().positive(),
    contents: z.string().min(1),
    rotation: z.number().default(0),
    styleName: z.string().default("Standard"),
    // CECOLOR — current colour stamped at the dispatch boundary. AutoCAD assigns
    // CECOLOR to ALL newly-created objects incl. TEXT; the text style colour is a
    // FALLBACK only when the entity colour is ByLayer. Optional → ByLayer in the
    // reducer when absent (legacy / tests).
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-mtext"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    insertionPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    nominalHeight: z.number().positive(),
    referenceWidth: z.number().positive(),
    contents: z.string().min(1),
    styleName: z.string().default("Standard"),
    // CECOLOR — current colour stamped at the dispatch boundary. AutoCAD assigns
    // CECOLOR to a newly-created MTEXT; the style colour is a fallback when the
    // entity colour is ByLayer. Optional → ByLayer in the reducer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-dimension"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    extLine1Point: z.object({
      x: z.number(),
      y: z.number(),
    }),
    extLine2Point: z.object({
      x: z.number(),
      y: z.number(),
    }),
    dimensionLinePoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    // Optional vertex point — only used by 3-point angular dimensions
    // (DIM3POINT, dimensionType=5). Maps to DXF group code 15/25/35 →
    // DimensionEntity.thirdPoint.
    thirdPoint: z
      .object({
        x: z.number(),
        y: z.number(),
        z: z.number().default(0),
      })
      .optional(),
    dimensionType: z.number().int().default(0),
    dimensionText: z.string().default(""),
    // Current DIMSTYLE (AutoCAD DIMSTYLE sysvar) stamped at the dispatch
    // boundary — the style a NEW dimension inherits. Mirrors the CECOLOR seam:
    // the interactive draft / command-line / AI paths omit it, and the dispatch
    // chokepoint (`stampCurrentDimStyleOntoCreateCommand`) stamps the persisted
    // current style (`defaultObjectProperties["Dimension style"]`). Optional →
    // the reducer falls back to the first style table entry when absent or when
    // the name does not resolve, preserving pre-stamp behaviour.
    styleName: z.string().optional(),
    // CECOLOR — current colour stamped at the dispatch boundary. AutoCAD assigns
    // CECOLOR to a newly-created DIMENSION (all variants route through this single
    // command, narrowed by dimensionType); the dimension style colour is a
    // fallback when the entity colour is ByLayer. Optional → ByLayer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-multileader"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    arrowPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    landingPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    textContents: z.string().min(1),
    // CECOLOR — current colour stamped at the dispatch boundary. AutoCAD assigns
    // CECOLOR to a newly-created MULTILEADER; the multileader style colour is a
    // fallback when the entity colour is ByLayer. Optional → ByLayer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  // MLEADEREDIT — Add Leader. Appends a new leader line with the given
  // arrow point to an existing multileader. The leader is appended to the
  // multileader's first leader root / first node.
  z.object({
    type: z.literal("geometry/add-leader-to-multileader"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    arrowPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
  }),
  // MLEADEREDIT — Remove Leader. Removes the leader line at the given
  // `leaderIndex` (flat index across all leaderRoots → nodes → lines).
  // If this is the last leader line and `confirmed` is not true, the
  // reducer rejects the command with a warning audit event so the UI
  // layer can present a confirm dialog. When the last leader is removed
  // with `confirmed === true`, the entire multileader entity is deleted.
  z.object({
    type: z.literal("geometry/remove-leader-from-multileader"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    leaderIndex: z.number().int().nonnegative(),
    confirmed: z.boolean().optional(),
  }),
  // Phase 9 — Grip-edit reducer commands. The grip-edit UI dispatches
  // these directly when the user drags one of the multileader's grip
  // handles (arrow / vertex / landing / dogleg / text). Each command
  // mutates exactly one slice of the multileader entity and goes
  // through the same shell-resync + enrichment pipeline as the Phase 6
  // Add/Remove leader commands so the renderer sees the new geometry
  // without a stale-shell window.
  z.object({
    type: z.literal("geometry/update-multileader-vertex"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    rootIndex: z.number().int().nonnegative(),
    nodeIndex: z.number().int().nonnegative(),
    lineIndex: z.number().int().nonnegative(),
    vertexIndex: z.number().int().nonnegative(),
    newPosition: z.object({
      x: z.number(),
      y: z.number(),
    }),
  }),
  z.object({
    type: z.literal("geometry/update-multileader-text-location"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    newLocation: z.object({
      x: z.number(),
      y: z.number(),
    }),
  }),
  z.object({
    type: z.literal("geometry/update-multileader-text-rotation"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    newRotation: z.number(),
  }),
  z.object({
    type: z.literal("geometry/update-multileader-dogleg"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    rootIndex: z.number().int().nonnegative(),
    nodeIndex: z.number().int().nonnegative(),
    doglegVector: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    doglegLength: z.number().nonnegative(),
  }),
  // Phase 9 — Remove a single intermediate vertex from a leader line.
  // Used by the right-click context menu's "Remove Vertex" item. The
  // command is silently ignored if the line has fewer than 3 vertices
  // (we never want to leave a leader line with less than 2 points).
  z.object({
    type: z.literal("geometry/remove-multileader-vertex"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    rootIndex: z.number().int().nonnegative(),
    nodeIndex: z.number().int().nonnegative(),
    lineIndex: z.number().int().nonnegative(),
    vertexIndex: z.number().int().nonnegative(),
  }),
  // B18 — Insert a vertex at `newPosition` after `vertexIndex` in the
  // target leader line. Used by the right-click context menu's
  // "Add Vertex" item. Out-of-range tuples silently no-op (matching
  // the rest of the multileader edit family).
  z.object({
    type: z.literal("geometry/add-multileader-vertex"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    rootIndex: z.number().int().nonnegative(),
    nodeIndex: z.number().int().nonnegative(),
    lineIndex: z.number().int().nonnegative(),
    /**
     * The vertex AFTER which the new vertex is inserted. The new
     * vertex's index in the resulting array is `vertexIndex + 1`.
     */
    vertexIndex: z.number().int().nonnegative(),
    newPosition: z.object({
      x: z.number(),
      y: z.number(),
    }),
  }),
  // REVCLOUD — revision cloud. AutoCAD prompts "Specify first point or
  // [Arc length/Object/Rectangular/Polygonal/Freehand/Style/Modify]"
  // (Autodesk REVCLOUD command reference). REVCLOUD-MODES carries the
  // creation method explicitly:
  //   Rectangular — corner1 + corner2 (legacy two-corner flow; default).
  //   Polygonal   — vertices[] (>=3 picked polygon vertices; arcs follow
  //                 each polygon segment).
  //   Freehand    — vertices[] (>=3 path points; the draft layer samples
  //                 the guided cursor path at ~arcChordLength spacing and
  //                 closes when the cursor returns to the start point).
  //                 Persisted creationMode is "Polygonal": the audited
  //                 RevcloudProps XData mode enum (dxf-io entityParser
  //                 REVCLOUD_MODE_BY_CODE 0=Polygonal/1=Rectangular/
  //                 2=Object) carries no Freehand code — a committed
  //                 freehand path IS a polygonal vertex chain.
  //   Object      — sourceEntityId of an existing CLOSED entity (circle /
  //                 ellipse / closed polyline / closed spline). The reducer
  //                 traces the source outline, REPLACES the source entity
  //                 (AutoCAD deletes the source by default) and honours
  //                 reverseDirection (AutoCAD "Reverse direction [Yes/No]"
  //                 prompt — flips which side the arc bulges point to).
  z.object({
    type: z.literal("geometry/create-revcloud"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    creationMode: z
      .enum(["Rectangular", "Polygonal", "Freehand", "Object"])
      .default("Rectangular"),
    corner1: z
      .object({
        x: z.number(),
        y: z.number(),
      })
      .optional(),
    corner2: z
      .object({
        x: z.number(),
        y: z.number(),
      })
      .optional(),
    vertices: z
      .array(
        z.object({
          x: z.number(),
          y: z.number(),
        }),
      )
      .min(3)
      .optional(),
    sourceEntityId: z.string().min(1).optional(),
    reverseDirection: z.boolean().default(false),
    // REVCLOUD Style keyword — Normal | Calligraphy (RevcloudProps 1070
    // style code 0/1; Autodesk REVCLOUD "Style" option).
    revisionCloudStyle: z.enum(["Normal", "Calligraphy"]).default("Normal"),
    arcChordLength: z.number().positive().default(1),
    // CECOLOR — current colour stamped at the dispatch boundary (REVCLOUD
    // produces a Revision Cloud entity; AutoCAD assigns CECOLOR). Optional →
    // ByLayer in the reducer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }).refine(
    (cmd) => {
      switch (cmd.creationMode) {
        case "Rectangular":
          return cmd.corner1 !== undefined && cmd.corner2 !== undefined;
        case "Polygonal":
        case "Freehand":
          return cmd.vertices !== undefined;
        case "Object":
          return cmd.sourceEntityId !== undefined;
      }
    },
    {
      message:
        "REVCLOUD payload incomplete for its creationMode: Rectangular needs corner1+corner2, Polygonal/Freehand need vertices[>=3], Object needs sourceEntityId",
    },
  ),
  // WIPEOUT — closed polygon with opaque background fill that hides
  // entities rendered beneath it. Rectangular first-pass:
  // {corner1, corner2} expand into 4 vertices in the reducer; the
  // optional `vertices` array supports the future polygon-pick mode
  // and AI-driven WIPEOUT authoring without a second command type.
  z.object({
    type: z.literal("geometry/create-wipeout"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    vertices: z
      .array(
        z.object({
          x: z.number(),
          y: z.number(),
        }),
      )
      .min(3),
    frame: z.boolean().default(true),
    // CECOLOR — current colour stamped at the dispatch boundary (WIPEOUT is a
    // persisted entity; AutoCAD assigns CECOLOR). Optional → ByLayer.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-hatch"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    // ── Rectangular corner-pick mode (legacy) ──
    // Either both corners are present OR `boundaryPaths` is provided
    // (internal-point pick mode). The reducer prefers `boundaryPaths`
    // when present and falls back to the rectangular corners path.
    boundaryCorner1: z
      .object({
        x: z.number(),
        y: z.number(),
      })
      .optional(),
    boundaryCorner2: z
      .object({
        x: z.number(),
        y: z.number(),
      })
      .optional(),
    // ── Internal-pick mode (BHATCH "Pick" point) ──
    // Pre-computed boundary paths assembled from `findBoundaryFromSeed`
    // by the state machine. Each path is a list of edges (line / arc).
    // Mirrors the structure of the underlying HATCH entity loops.
    boundaryPaths: z
      .array(
        z.object({
          pathTypeFlag: z.number().int().default(1),
          edges: z.array(
            z.discriminatedUnion("edgeType", [
              z.object({
                edgeType: z.literal("line"),
                start: z.object({ x: z.number(), y: z.number() }),
                end: z.object({ x: z.number(), y: z.number() }),
              }),
              z.object({
                edgeType: z.literal("arc"),
                center: z.object({ x: z.number(), y: z.number() }),
                radius: z.number(),
                startAngle: z.number(),
                endAngle: z.number(),
                counterclockwise: z.boolean().default(false),
              }),
            ]),
          ),
        }),
      )
      .optional(),
    /**
     * Seed points used to detect the boundaries — preserved on the
     * resulting HATCH entity for round-trip fidelity (HATCH XDATA in
     * AutoCAD records the click points).
     */
    seedPoints: z
      .array(z.object({ x: z.number(), y: z.number() }))
      .optional(),
    patternName: z.string().default("ANSI31"),
    patternScale: z.number().positive().default(1),
    patternAngle: z.number().default(0),
    // CECOLOR — current colour stamped at the dispatch boundary (a new HATCH
    // inherits CECOLOR in AutoCAD). Optional → ByLayer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  z.object({
    type: z.literal("geometry/create-block-insert"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    blockName: z.string().min(1),
    insertionPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    scaleX: z.number().default(1),
    scaleY: z.number().default(1),
    rotation: z.number().default(0),
    // CECOLOR — current colour stamped at the dispatch boundary onto the INSERT
    // ENTITY. A ByLayer insert colour means children render with their own
    // colours; an explicit CECOLOR on the INSERT colours its BYBLOCK children
    // with the insert colour (AutoCAD-correct — stamp the insert entity colour,
    // do NOT recurse into the block definition). Optional → ByLayer when absent.
    layer: z.string().optional(),
    color: entityColorSchema.optional(),
  }),
  // T6b — AI-dispatch coalescing. Batches K `geometry/create-block-insert`
  // copies (an AutoCAD MINSERT row×column fan-out) into ONE reducer pass
  // instead of K separate dispatches, so a 50,000-copy MINSERT pays ONE
  // `assembleDrawingDocument` validate instead of 50,000. Internal-only —
  // synthesized EXCLUSIVELY by the AI safe-contour's dispatch coalescer
  // (`aiSafeContour.ts` `dispatchAiCommandSequence`) AFTER the CC-2a
  // `assertAiCallableAction` guard has already run on the original per-copy
  // commands; it is NOT part of any AI-facing `.action` schema in
  // `packages/ai-commands` (those are keyed on `action`, a disjoint
  // discriminator from this `EditorCommand.type`), so an AI proposal can
  // never target it directly.
  z.object({
    type: z.literal("geometry/create-block-inserts-batch"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    blockName: z.string().min(1),
    // CECOLOR — shared by every insert in the batch (all constituent creates in a
    // coalesced run share spaceId/blockName/color BY CONSTRUCTION — see
    // aiSafeContour.ts's grouping predicate). Same ByLayer-default semantics as
    // the single-insert command.
    color: entityColorSchema.optional(),
    // T7 (agent-bridge) local Codex round-18 P2 (post-T6b-merge regression
    // catch) — the CLAYER sibling of `color` directly above: shared by every
    // insert in the batch, same "0"-default semantics as the single-insert
    // command's `layer` field.
    layer: z.string().optional(),
    // One entry per block-insert copy, in dispatch order — the SAME per-copy
    // fields `geometry/create-block-insert` carries (minus spaceId/blockName/color,
    // hoisted to the batch level since a coalesced run shares them).
    inserts: z
      .array(
        z.object({
          insertionPoint: z.object({ x: z.number(), y: z.number() }),
          scaleX: z.number().default(1),
          scaleY: z.number().default(1),
          rotation: z.number().default(0),
        }),
      )
      .min(1),
  }),
  // ── BLOCK definition authoring (Phase 1 wire-up) ──
  // Full BLOCK command pipeline: create definition + optional insert.
  // The caller (App.tsx handleBlockDefinitionConfirm) collects sourceEntities
  // from the active drawing document, clones them into blockDef.entities, and
  // then dispatches this command. The reducer accepts the prepared blockDef
  // and blockRecord as-is — validateBlockName runs again inside
  // createBlockDefinitionMutation for defence in depth.
  z.object({
    type: z.literal("geometry/create-block-definition"),
    source: commandSourceSchema,
    blockDef: z.object({
      name: z.string().min(1),
      handle: z.string().default(""),
      ownerHandle: z.string().optional(),
      layer: z.string().default("0"),
      flags: z.number().int().default(0),
      basePoint: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number().default(0),
      }),
      xrefPathName: z.string().default(""),
      description: z.string().default(""),
      // Pre-cloned block child entities assembled by the caller.
      // z.unknown() keeps the contract package free of drawing-model
      // coupling; the geometryReducer passes them through to the
      // mutation which re-parses via blockDefinitionSchema.
      entities: z.array(z.unknown()).default([]),
    }),
    blockRecord: z.object({
      name: z.string().min(1),
      // Ceiling 21 (US Survey Feet) — matches blockRecordSchema / $INSUNITS.
      insertionUnits: z.number().int().min(0).max(21).default(0),
      explodable: z.boolean().default(true),
      scalable: z.enum(["uniform", "non-uniform"]).default("non-uniform"),
      layoutHandle: z.string().optional(),
      isAnnotative: z.boolean().default(false),
      description: z.string().default(""),
      dynamicBlockData: z.unknown().optional(),
    }),
    postAction: z.enum(["retain", "convert", "delete"]),
    sourceEntityIds: z.array(z.string()).default([]),
    convertSpaceId: z.string().optional(),
    convertInsertPoint: z.object({ x: z.number(), y: z.number() }).optional(),
    convertInsertId: z.string().optional(),
    // CECOLOR round-6 FINDING C — the current colour stamped onto the
    // postAction=convert replacement INSERT instance ONLY. The block DEFINITION
    // stays colour-less (a BLOCK record is a container, never an entity); but the
    // INSERT it appends is a drawn entity that, like the regular
    // `geometry/create-block-insert` path, inherits CECOLOR. The dispatch
    // chokepoint stamps this from `getCeColor()` in the convert path (the
    // top-level `color` is deliberately NEVER stamped on create-block-definition
    // so the definition is not coloured). Omitted → the reducer defaults the
    // INSERT to ByLayer (legacy / non-stamped path).
    convertInsertColor: entityColorSchema.optional(),
    // T7 (agent-bridge) local Codex round-17 P2 (github PR review) — the
    // CLAYER sibling of `convertInsertColor` directly above: the
    // postAction=convert replacement INSERT ALSO inherits the current layer
    // (like the regular `geometry/create-block-insert` path), NOT the
    // block-definition container (which stays on its own authored `layer`,
    // never stamped). Omitted -> the reducer defaults the INSERT to "0"
    // (legacy / non-stamped path).
    convertInsertLayer: z.string().optional(),
  }),
  // ── BLOCK definition removal (history-internal) ──
  // The undo inverse of `geometry/create-block-definition`: removes the
  // BlockDefinition + BlockRecord registered by the forward from the document's
  // block table. It is dispatched ONLY by `computeInverse` during undo/redo
  // replay (never typed at the command line — there is no AutoCAD "delete block
  // definition" command; PURGE removes UNREFERENCED blocks, which is a different
  // contract). The removal is unconditional: the undo batch replays the INSERT
  // inverses BEFORE this command, so the block is already unreferenced when it
  // runs, and re-creating the same name on redo is collision-free. Mirrors how
  // block defs are ADDED (geometry/create-block-definition) so undo is a precise
  // structural reversal — not a state-dependent PURGE. Allowlisted for registry
  // coverage alongside the other history-internal restore commands.
  z.object({
    type: z.literal("geometry/remove-block-definition"),
    source: commandSourceSchema,
    name: z.string().min(1),
  }),
  // EXPLODE an INSERT entity into its block's child entities.
  // Subagent-3's explodeInsertMutation does the heavy lifting (handles
  // BYBLOCK inheritance, matrix composition, Circle/Arc → Ellipse when
  // scaling is non-uniform, and ATTRIB → Text promotion). The command
  // only needs the INSERT id and nesting resolution mode.
  z.object({
    type: z.literal("geometry/explode-insert"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
    resolveNested: z.enum(["none", "one-level"]).default("one-level"),
  }),
  // ── ATTDEF authoring (Phase 2.1) — only valid inside a BEDIT session.
  // Adds a new AttdefEntity to the named block's definition. The reducer
  // rejects the command (silently, with a toast appended by App.tsx) when
  // the active space is not `block-edit` or when the block name does not
  // match the active BEDIT target. All AutoCAD-parity fields are captured.
  z.object({
    type: z.literal("geometry/create-attdef"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    attdef: z.object({
      tag: z.string().min(1),
      prompt: z.string().default(""),
      defaultValue: z.string().default(""),
      insertionPoint: z.object({ x: z.number(), y: z.number() }),
      height: z.number().positive(),
      rotation: z.number().default(0),
      widthFactor: z.number().default(1),
      obliqueAngle: z.number().default(0),
      styleName: z.string().default("Standard"),
      hJustification: z.number().int().min(0).max(5).default(0),
      vJustification: z.number().int().min(0).max(3).default(0),
      alignmentPoint: z.object({ x: z.number(), y: z.number() }).optional(),
      flags: z.object({
        invisible: z.boolean().default(false),
        constant: z.boolean().default(false),
        verify: z.boolean().default(false),
        preset: z.boolean().default(false),
        lockPosition: z.boolean().default(false),
        multiline: z.boolean().default(false),
      }),
      backgroundFill: z.boolean().default(false),
      backgroundColor: z.string().optional(),
      fillBoxScale: z.number().default(1.5),
    }),
    // T7 (agent-bridge) local Codex round-16 P2 (github PR review) — an ATTDEF
    // authored in BEDIT is a DRAWN entity that ALSO inherits CLAYER, the same
    // way it inherits CECOLOR (round-5 FINDING B, immediately below). Optional
    // (no ByLayer-style "0" default) so the dispatch chokepoint's
    // `stampCurrentLayerOntoCreateCommand` can stamp the live CLAYER; the
    // toolbar dialog (`useAttributeDialogs.ts`) already reads CLAYER
    // explicitly, so this only changes the AI/programmatic-omitted case.
    layer: z.string().optional(),
    // CECOLOR — an ATTDEF authored in BEDIT is a DRAWN entity that inherits the
    // current color (round-5 FINDING B). The optional typed `EntityColor` union
    // (matching every other stampable create) lets the dispatch chokepoint stamp
    // the live cecolor; omitted → the reducer coerces undefined → ByLayer.
    color: entityColorSchema.optional(),
    linetype: z.string().default("ByLayer"),
    lineweight: z.string().default("ByLayer"),
  }),
  // ── Single-tag ATTRIB update (DDATTE / ATTEDIT fallback).
  // Matches one ATTRIB on the named INSERT by tag; if no ATTRIB exists
  // and the ATTDEF is non-constant the reducer creates a new ATTRIB
  // entry inheriting the ATTDEF's defaults. Constant tags are rejected
  // upstream by the editor UI (hidden / disabled) and by the mutation.
  z.object({
    type: z.literal("geometry/update-attribute-value"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
    tag: z.string().min(1),
    newValue: z.string().optional(),
    overrides: z
      .object({
        overrideHeight: z.number().optional(),
        overrideRotation: z.number().optional(),
        overridePosition: z.object({ x: z.number(), y: z.number() }).optional(),
        overrideStyle: z.string().optional(),
        invisible: z.boolean().optional(),
      })
      .optional(),
  }),
  // ── Batch ATTRIB update (DDATTE / EATTEDIT "Save" button).
  // Applies an array of per-tag updates sequentially. Lets EATTEDIT
  // Apply/OK commit every visible row in one command so undo treats
  // the whole edit session as a single unit.
  z.object({
    type: z.literal("geometry/update-attribute-values"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
    updates: z.array(
      z.object({
        tag: z.string().min(1),
        newValue: z.string().optional(),
        overrides: z
          .object({
            overrideHeight: z.number().optional(),
            overrideRotation: z.number().optional(),
            overridePosition: z
              .object({ x: z.number(), y: z.number() })
              .optional(),
            overrideStyle: z.string().optional(),
            invisible: z.boolean().optional(),
          })
          .optional(),
      }),
    ),
    /**
     * Optional property patch applied to the INSERT's common attributes
     * (layer, color, linetype, lineweight) — EATTEDIT Properties tab.
     * `undefined` leaves the existing value intact.
     */
    insertProperties: z
      .object({
        layer: z.string().optional(),
        color: z.string().optional(),
        linetype: z.string().optional(),
        lineweight: z.string().optional(),
      })
      .optional(),
  }),
  // ── BATTMAN — remove an ATTDEF from a block (Phase 2.4).
  // Optional cascade removes matching ATTRIB rows from every INSERT
  // referencing the block. Default true matches AutoCAD BATTMAN.
  z.object({
    type: z.literal("geometry/remove-attdef"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    attdefId: z.string().min(1),
    removeFromInserts: z.boolean().default(true),
  }),
  // ── BATTMAN — update ATTDEF properties (Phase 2.4).
  // Only the fields present in `updates` are applied; others are left
  // untouched. `flags` is a partial of AttdefFlags — boolean entries
  // override per-bit, missing keys keep the previous bit.
  z.object({
    type: z.literal("geometry/update-attdef"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    attdefId: z.string().min(1),
    updates: z.object({
      prompt: z.string().optional(),
      defaultValue: z.string().optional(),
      height: z.number().positive().optional(),
      rotation: z.number().optional(),
      widthFactor: z.number().optional(),
      obliqueAngle: z.number().min(-85).max(85).optional(),
      styleName: z.string().optional(),
      hJustification: z.number().int().min(0).max(5).optional(),
      vJustification: z.number().int().min(0).max(3).optional(),
      flags: z
        .object({
          invisible: z.boolean().optional(),
          constant: z.boolean().optional(),
          verify: z.boolean().optional(),
          preset: z.boolean().optional(),
          lockPosition: z.boolean().optional(),
          multiline: z.boolean().optional(),
        })
        .optional(),
      backgroundFill: z.boolean().optional(),
      backgroundColor: z.string().optional(),
      fillBoxScale: z.number().optional(),
    }),
  }),
  // ── BATTMAN — rename an ATTDEF tag (Phase 2.4). Cascades through
  // every INSERT referencing the block, rewriting matching ATTRIB
  // rows' `tag` so user-authored values survive the rename.
  z.object({
    type: z.literal("geometry/rename-attdef-tag"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    attdefId: z.string().min(1),
    newTag: z.string().min(1),
  }),
  // ── BATTMAN — reorder ATTDEFs inside a block (Phase 2.4).
  // `attdefIds` must list every ATTDEF id in the block exactly once;
  // the new order dictates the INSERT prompt sequence.
  z.object({
    type: z.literal("geometry/reorder-attdefs"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    attdefIds: z.array(z.string().min(1)).min(1),
  }),
  // ── ATTSYNC — sync ATTRIB rows with the ATTDEF template (Phase 2.5).
  // Without targetInsertIds every INSERT of the block is updated;
  // passing a subset restricts the sync. Preserves user-authored
  // ATTRIB values for tags that still exist, adds defaults for new
  // ATTDEFs and drops orphan / constant ATTRIB rows.
  z.object({
    type: z.literal("geometry/attsync"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    targetInsertIds: z.array(z.string().min(1)).optional(),
  }),
  // ── Dynamic block authoring (DYN-1.7 + DYN-1.8) ────────────────
  // Parameter / action / visibility-state CRUD commands. Reducer-level
  // enforcement requires `block-edit` to be the active space and for
  // `blockName` to match the active BEDIT target; otherwise the
  // command is a silent no-op (App.tsx surfaces a toast). Payloads are
  // kept `z.unknown()` where the drawing-model schema is more
  // expressive than anything the contract can validate without pulling
  // in the full drawing-model dependency — the reducer re-parses via
  // `dynamicParameterSchema` / `dynamicActionSchema` at the mutation
  // boundary.
  z.object({
    type: z.literal("geometry/add-dynamic-parameter"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameter: z.unknown(),
  }),
  z.object({
    type: z.literal("geometry/update-dynamic-parameter"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("geometry/remove-dynamic-parameter"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/add-dynamic-action"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    action: z.unknown(),
  }),
  z.object({
    type: z.literal("geometry/update-dynamic-action"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("geometry/remove-dynamic-action"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/add-visibility-state"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
    stateName: z.string().min(1),
    invisibleEntityIds: z.array(z.string().min(1)).default([]),
  }),
  z.object({
    type: z.literal("geometry/rename-visibility-state"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
    newName: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/delete-visibility-state"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/reorder-visibility-states"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateIds: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("geometry/set-default-visibility-state"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/toggle-visibility-state-entity"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
    entityId: z.string().min(1),
    visible: z.boolean(),
  }),
  // ── Phase K.4 — Visibility States Manager additions ──────────────
  // BVSTATE-current state pick (pre-existing setDefault sibling); the
  // current pointer is what the renderer + Properties palette read.
  z.object({
    type: z.literal("geometry/set-current-visibility-state"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
  }),
  // BVHIDE — hide entities in the current state only.
  z.object({
    type: z.literal("geometry/bvhide"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  // BVSHOW (current state only) — make entities visible in current state.
  z.object({
    type: z.literal("geometry/bvshow"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    stateId: z.string().min(1),
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  // BVSHOW _All — make entities visible in EVERY state.
  z.object({
    type: z.literal("geometry/bvshow-all"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  // ── DYN-3 Batch C — Chain Actions (pipeline authoring) ─────────
  // BEDIT-gated — same enforcement rules as the add/remove-action
  // commands above. Payloads are fully typed here because the shape
  // is small (arrays of action ids); no need to defer to drawing-model.
  z.object({
    type: z.literal("geometry/create-chain-group"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionIds: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("geometry/add-action-to-chain-group"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
    groupIndex: z.number().int().min(0).optional(),
  }),
  z.object({
    type: z.literal("geometry/remove-action-from-chain"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/reorder-chain-group"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    groupIndex: z.number().int().min(0),
    actionIds: z.array(z.string().min(1)).min(1),
  }),
  // ── DYN-3 Batch E — Geometric constraint addition ────────────────
  // BEDIT-gated. `constraint` carries the full GeometricConstraint
  // record (kind + entityIds + optional referenceLineId). Reducer
  // re-parses via geometricConstraintSchema before applying.
  z.object({
    type: z.literal("geometry/add-geometric-constraint"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    constraint: z.unknown(),
  }),
  // ── DYN-2 B.2 — Lookup Table + Block Properties Table commands ──
  // Same gating as the authoring commands (reducer requires block-edit
  // active and a matching blockName). Payload is z.unknown() wherever
  // the drawing-model schema is more expressive; reducer re-parses at
  // the mutation boundary via lookupTableSchema / blockPropertyEntrySchema.
  z.object({
    type: z.literal("geometry/create-lookup-table"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    table: z.unknown(),
  }),
  z.object({
    type: z.literal("geometry/update-lookup-table"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    tableId: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("geometry/delete-lookup-table"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    tableId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/set-block-properties-table"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    table: z.unknown(),
  }),
  z.object({
    type: z.literal("geometry/add-block-property"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    entry: z.unknown(),
  }),
  z.object({
    type: z.literal("geometry/update-block-property"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    key: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("geometry/remove-block-property"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    key: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/reorder-block-properties"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    orderedKeys: z.array(z.string().min(1)).min(1),
  }),
  // ── Phase N.5 — Block Properties Manager (BPM) commands ──────────
  // Drawing-wide parameter / action editing dispatched from the
  // BLOCKPROPERTIESMANAGER dialog. Unlike the BEDIT-gated authoring
  // commands above, these handlers do NOT require an active block-edit
  // session — they operate on any block by name. The reducer still
  // validates the target block exists and re-runs `validateDynamicMetadata`
  // before committing.
  z.object({
    type: z.literal("geometry/rename-block-parameter"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    newName: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/update-block-parameter-description"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
    description: z.string(),
  }),
  z.object({
    type: z.literal("geometry/toggle-parameter-show-in-palette"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    parameterId: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/reorder-block-parameters"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    newOrder: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("geometry/rename-block-action"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
    newName: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/update-block-action-description"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    actionId: z.string().min(1),
    description: z.string(),
  }),
  z.object({
    type: z.literal("geometry/reorder-block-actions"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    newOrder: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("geometry/create-arrayrect"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    // Generated by caller (dispatch layer) — keeps reducer deterministic.
    arrayGroupId: z.string().min(1),
    basePoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    rowCount: z.number().int().positive(),
    columnCount: z.number().int().positive(),
    rowSpacing: z.number(),
    columnSpacing: z.number(),
    axisAngle: z.number().default(0),
  }),
  z.object({
    type: z.literal("geometry/create-arraypolar"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    // Generated by caller (dispatch layer) — keeps reducer deterministic.
    arrayGroupId: z.string().min(1),
    centerPoint: z.object({
      x: z.number(),
      y: z.number(),
    }),
    itemCount: z.number().int().min(2),
    fillAngle: z.number().default(360),
    rotateItems: z.boolean().default(true),
    rowCount: z.number().int().min(1).default(1),
    rowSpacing: z.number().default(0),
  }),
  z.object({
    type: z.literal("geometry/create-arraypath"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    // Generated by caller (dispatch layer) — keeps reducer deterministic.
    arrayGroupId: z.string().min(1),
    pathEntityId: z.string().min(1),
    method: z.enum(["divide", "measure"]),
    // Either itemCount (for "divide") or itemSpacing (for "measure")
    // is required; at least one must be present. Validation for this
    // conditional is handled by the reducer — schema-level optional.
    itemCount: z.number().int().min(1).optional(),
    itemSpacing: z.number().positive().optional(),
    alignItems: z.boolean().default(true),
  }),
  z.object({
    type: z.literal("geometry/arrayedit"),
    source: commandSourceSchema,
    arrayGroupId: z.string().min(1),
    mode: z.enum(["rect", "polar", "path"]),
    // Parameters — exactly one of rect / polar / path should be set
    // based on `mode`. Validation for this conditional happens in the
    // reducer; schema-level all optional.
    rect: z
      .object({
        rowCount: z.number().int().positive(),
        columnCount: z.number().int().positive(),
        rowSpacing: z.number(),
        columnSpacing: z.number(),
        axisAngle: z.number().default(0),
      })
      .optional(),
    polar: z
      .object({
        centerPoint: z.object({
          x: z.number(),
          y: z.number(),
        }),
        itemCount: z.number().int().min(2),
        fillAngle: z.number().default(360),
        rotateItems: z.boolean().default(true),
        rowCount: z.number().int().min(1).default(1),
        rowSpacing: z.number().default(0),
      })
      .optional(),
    path: z
      .object({
        method: z.enum(["divide", "measure"]),
        itemCount: z.number().int().min(1).optional(),
        itemSpacing: z.number().positive().optional(),
        alignItems: z.boolean().default(true),
        pathEntityId: z.string().min(1),
      })
      .optional(),
  }),
  z.object({
    // HATCHEDIT — edit a placed HATCH entity's wired pattern fields through the
    // Command Engine (mirrors `geometry/arrayedit`: a select-then-field-prompt
    // CLI flow whose commit lands here). The fields map 1:1 onto the typed
    // `hatchEntitySchema` (drawing-model): patternName / patternScale /
    // patternAngle / hatchStyle / patternType / associative. The reducer
    // routes them through the SAME `translateExtendedEntityPropertyChanges` →
    // `patchEntityById` path the `properties/apply` palette edit uses (no
    // parallel patch routine), so undo/redo + DXF export carry the edit.
    //
    // Gradient-fill edit, boundary Add/Remove and Inherit-Properties origin are
    // INTENTIONALLY out of scope here — their mutation layer does not exist yet
    // (see docs/test-gaps/cmd-hatch.md DEFERRED rows). Shipping a stub would
    // violate "ничего не упрощать" (CLAUDE.md ПРИОРИТЕТ #1).
    type: z.literal("geometry/hatchedit"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    // All fields optional — at least one must be present. That ">=1 field"
    // conditional is validated by the reducer (an empty edit is a no-op),
    // mirroring the rect/polar/path "exactly one" note on geometry/arrayedit.
    patternName: z.string().min(1).optional(),
    patternScale: z.number().positive().optional(),
    patternAngle: z.number().optional(),
    // hatchStyle (DXF group 75: 0=Normal/1=Outer/2=Ignore) and patternType
    // (DXF group 76: 0=User-defined/1=Predefined/2=Custom) are plain ints in
    // the typed model — no enum exists in this contract, so they stay
    // `z.number().int()` matching `hatchEntitySchema` (drawing-model model.ts).
    hatchStyle: z.number().int().optional(),
    patternType: z.number().int().optional(),
    associative: z.boolean().optional(),
    // N8 HATCH-DOUBLE-PROPERTY-DESCRIPTOR (@codex P2 round 6) — DXF group 77
    // (pattern double-line flag). Plain scalar, applied RAW like hatchStyle/
    // patternType/associative above — no derivation, no coupling. Carried
    // by BOTH forward `geometry/hatchedit` edits AND the undo inverse
    // (editorStore.ts's single-hatch typed restore): "Double" is a
    // typed-writer-owned `properties/apply` field whose write can be
    // REJECTED per-instance (read-only unless Pattern type = User defined),
    // so an undo that ALSO reverts "Pattern type" back to a non-user-defined
    // value would have its `doubleLine` restore silently rejected by that
    // SAME gate if replayed as a second `properties/apply` — this raw
    // scalar path bypasses per-field validation entirely (mirrors the
    // hatchStyle/patternType/associative precedent), guaranteeing undo
    // restores the EXACT pre-mutation value regardless of the gate.
    doubleLine: z.boolean().optional(),
    // DXF group 70 — solid-fill flag. Normally DERIVED from patternName
    // (SOLID ⇒ true) by the forward translator, so callers/AI never need to
    // set it. It is carried explicitly ONLY by the undo inverse so a hatch
    // whose source solidFill did NOT match its patternName (inconsistent
    // imported DXF) is restored VERBATIM rather than re-derived. Optional.
    solidFill: z.boolean().optional(),
    // ── Gradient / custom pattern-line restore (Codex P2-d) ──
    // A pattern-CLASS change (the forward translator deriving a NEW patternName)
    // CLEARS the now-inapplicable `gradient` (DXF 450-block) and
    // `customPatternLines` (DXF 78-block) payloads — a stale gradient otherwise
    // WINS over the new pattern in the renderer / PDF / DXF export. These two
    // fields are carried explicitly ONLY by the undo inverse so the PRE-mutation
    // gradient / custom lines are restored VERBATIM on undo (preserve-source —
    // they cannot be re-derived from the pattern name). `null` is the explicit
    // "the source had none, restore to absent" signal (the reducer maps it to
    // `undefined` on the typed entity); omitted ⇒ the field is left untouched.
    // Callers / AI never set these (mirrors `solidFill`). Optional + nullable.
    gradient: hatchGradientSchema.nullable().optional(),
    customPatternLines: z.array(hatchPatternLineSchema).nullable().optional(),
  }),
  z.object({
    type: z.literal("geometry/create-viewport"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    corner1: z.object({
      x: z.number(),
      y: z.number(),
    }),
    corner2: z.object({
      x: z.number(),
      y: z.number(),
    }),
    // Model-space extent supplied by the draft state machine so the
    // reducer can auto-fit the viewport to the current model contents
    // (AutoCAD MVIEW FIT / corner parity). Optional so legacy callers
    // and AI replays without a pre-computed extent still succeed; in
    // that case the viewport falls back to viewHeight=100, center=0,0.
    modelExtent: z
      .object({
        minX: z.number(),
        minY: z.number(),
        maxX: z.number(),
        maxY: z.number(),
      })
      .optional(),
    // T7 (agent-bridge) local Codex round-17 P2 (github PR review) — CLAYER
    // stamped at the dispatch boundary onto the new VIEWPORT frame entity
    // (`stampCurrentLayerOntoCreateCommand`, editorStoreStyleTables.ts). A
    // newly-created MVIEW entity follows CLAYER exactly like every other
    // entity in AutoCAD — distinct from `create-viewport`'s EXCLUSION from
    // CECOLOR (that exclusion is about the VP-frame's border COLOUR being a
    // separate render-gated concern; plain layer-field assignment carries no
    // such complication). Optional so a command omitting it (legacy / tests)
    // defaults to "0" in the reducer.
    layer: z.string().optional(),
  }),
  z.object({
    type: z.literal("geometry/move-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    delta: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/copy-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    delta: z.object({ x: z.number(), y: z.number() }),
  }),
  // AutoCAD COPY with explicit base point + second point. Each dispatch
  // materialises ONE copy of `sourceEntityIds` translated by
  // (secondPoint − basePoint). The draft state machine loops by
  // re-dispatching with a fresh `secondPoint` until the user presses
  // Enter / Escape. Distinct from `geometry/copy-entities` (delta-only)
  // so the audit log / AI replay captures the exact base + second pair
  // and can reconstruct the command without needing live state. The
  // pure mutation lives in `packages/command-engine/src/copyMutation.ts`.
  z.object({
    type: z.literal("geometry/copy"),
    source: commandSourceSchema,
    sourceEntityIds: z.array(z.string().min(1)).min(1),
    basePoint: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
    secondPoint: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
  }),
  z.object({
    type: z.literal("geometry/mirror-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    mirrorLine1: z.object({ x: z.number(), y: z.number() }),
    mirrorLine2: z.object({ x: z.number(), y: z.number() }),
    deleteSource: z.boolean().default(false),
    // MIRRTEXT system variable, frozen onto the command at dispatch from the
    // live sysvar store (AutoCAD freezes the sysvar at execution time). 0 =
    // text stays readable (insertion point reflects, glyphs not flipped); 1 =
    // text mirrors fully like ordinary geometry. Default 0 = AutoCAD default,
    // so a command without it (legacy / test dispatch) keeps text readable.
    mirrtext: z.number().int().min(0).max(1).default(0),
    // UNDO-of-readable-mirror restore map (entityId → original rotation in
    // degrees). Populated ONLY by computeInverse for the deleteSource=true
    // MIRRTEXT=0 readable-text case: the position re-mirror is an exact
    // involution, but the readable-rotation re-derivation is NOT self-inverse
    // for text whose baseline points backward (rotation ∈ (90°,270°)) — a plain
    // re-mirror would land 180° off. The reducer, when it sees this map, applies
    // the exact original rotation to each listed entity AFTER the coordinate
    // re-mirror, so MIRROR→UNDO restores position AND rotation exactly. Absent
    // on every forward (user/AI) dispatch — only undo carries it.
    restoreRotations: z.record(z.string(), z.number()).optional(),
    // UNDO-of-readable-mirror restore map for MTEXT X-axis direction vectors
    // (entityId → original {x,y,z}). Codex #342 F3: the readable mirror of an
    // MTEXT recomputes its `xAxisDirection` (group 11/21/31) to the reflected,
    // readable unit vector — which, like the rotation re-derivation, is NOT
    // self-inverse for a backward-facing source. So computeInverse snapshots the
    // PRE-mutation `xAxisDirection` of every MTEXT it touches and the reducer
    // restores it exactly AFTER the coordinate re-mirror, making MIRROR→UNDO
    // bit-exact for the direction vector too. Populated ONLY by computeInverse
    // (deleteSource=true, MIRRTEXT=0, MTEXT with a custom direction); absent on
    // every forward dispatch.
    restoreXAxisDirs: z
      .record(
        z.string(),
        z.object({ x: z.number(), y: z.number(), z: z.number() }),
      )
      .optional(),
    // UNDO-of-mirror restore map for MTEXT OCS extrusion handedness (entityId →
    // original {x,y,z}, or `null` when the source MTEXT had NO extrusion field).
    // Codex #342 F3: MIRRTEXT=1 records a mirrored MTEXT's det=−1 handedness as
    // extrusion (0,0,−1). The Z-flip is involutive, but a source that started
    // WITHOUT an extrusion would otherwise undo to a present (0,0,1) — a spurious
    // group 210. So computeInverse snapshots the pre-mutation extrusion (null =
    // absent) and the reducer restores it exactly AFTER the coordinate re-mirror,
    // keeping the round-trip byte-exact. Populated ONLY by computeInverse
    // (deleteSource=true, MTEXT); absent on every forward dispatch.
    restoreExtrusions: z
      .record(
        z.string(),
        z
          .object({ x: z.number(), y: z.number(), z: z.number() })
          .nullable(),
      )
      .optional(),
    // MIRRTEXT-DIMENSION-MLEADER-ATTRIB — UNDO-of-readable-mirror restore map
    // for a Block entity's per-attribute WORLD `overrideRotation` (EATTEDIT
    // absolute rotation override, gated on `overridePosition` being set —
    // NOT on `overrideRotation` itself; local Codex read-only review P1).
    // entityId → { [attribute array index as a string key]: original
    // overrideRotation in degrees, or `null` when the source attribute had NO
    // overrideRotation field at all (undo restores absence, never an emitted
    // `0` — byte-faithful DXF ATTRIB group-50 round-trip). Same
    // not-self-inverse story as `restoreRotations` (the MIRRTEXT=0 readable
    // rule collapses a backward-pointing angle onto its forward twin), but
    // keyed per-ATTRIBUTE-INDEX rather than one scalar per entity because a
    // Block carries an ARRAY of attributes. Populated ONLY by computeInverse
    // (deleteSource=true, MIRRTEXT=0, a Block with ≥1 WORLD-placed
    // attribute); absent on every forward (user/AI) dispatch.
    restoreAttributeRotations: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
    // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2) —
    // UNDO-of-readable-mirror restore map for a Multileader's per-
    // annotative-context `textRotation` (the render — `multileaderFromEntity
    // .ts` — prefers an active context's OWN rotation over the flat field
    // above). entityId → { [annotativeContexts array index as a string key]:
    // original textRotation in degrees }. `mleaderContextDataSchema
    // .textRotation` is `.default(0)` (never optional), so every entry is
    // always a real number — no null-sentinel needed here, unlike
    // `restoreAttributeRotations`. Populated ONLY by computeInverse
    // (deleteSource=true, MIRRTEXT=0, a Multileader with ≥1 annotative
    // context); absent on every forward (user/AI) dispatch.
    restoreContextRotations: z
      .record(z.string(), z.record(z.string(), z.number()))
      .optional(),
    // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2,
    // round 4) — UNDO-of-readable-mirror restore map for a Multileader's
    // per-scale SECONDARY CONTEXT_DATA blocks' `context.mTextData.rotation`
    // (imported annotative MLEADERs only — `dxfBridge.entityConversion.ts`'s
    // `modelMLeaderSecondaryContextsToDxf` re-exports these verbatim, so
    // MIRROR must update them itself or a saved file keeps the pre-mirror
    // text angle on every scale but the active one). entityId →
    // { [secondaryContexts array index as a string key]: original
    // `mTextData.rotation`, or `null` when the source block's MTextData had
    // NO rotation field at all (`mleaderMTextDataSchema.rotation` is
    // `.optional()`, never `.default()` — group 42 byte-faithful round-trip,
    // same null-sentinel convention as `restoreAttributeRotations`, unlike
    // the always-present `restoreContextRotations` above). Populated ONLY by
    // computeInverse (deleteSource=true, MIRRTEXT=0, a Multileader with ≥1
    // secondary block carrying MTextData); absent on every forward (user/AI)
    // dispatch.
    restoreSecondaryContextRotations: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
    // GitHub Codex review PR #558 round 2 P2 — UNDO restore map for the SAME
    // Multileader per-scale SECONDARY CONTEXT_DATA blocks'
    // `context.mTextData.extrusion` (OCS handedness — MIRRTEXT=1
    // canonicalizes an explicit/tilted extrusion to `{0,0,-1}` or deletes it,
    // same as the primary MTEXT extrusion `restoreExtrusions` above). entityId
    // → { [secondaryContexts array index as a string key]: original
    // `mTextData.extrusion`, or `null` when the source block's MTextData had
    // NO extrusion field at all — same index-keyed / null-sentinel shape as
    // `restoreSecondaryContextRotations`, one field per secondary block since
    // rotation and extrusion are independent (a block can change one without
    // the other). Populated ONLY by computeInverse (deleteSource=true,
    // MIRRTEXT=1, a Multileader with ≥1 secondary block carrying MTextData);
    // absent on every forward (user/AI) dispatch.
    restoreSecondaryContextExtrusions: z
      .record(
        z.string(),
        z.record(
          z.string(),
          z.object({ x: z.number(), y: z.number(), z: z.number() }).nullable(),
        ),
      )
      .optional(),
    // GitHub Codex review PR #558 round 2 (round-20 local read-only
    // follow-up) P2 — UNDO restore map for the PRIMARY (not secondary)
    // context's `contextData.mTextData.extrusion` (`reflectMLeaderExtrusion`
    // in `geometryReducerEdit.ts` properly reflects the primary extrusion —
    // round-17's own fix — but its presence-preserving omit-when-canonical
    // -default rule means a source that EXPLICITLY carried the canonical
    // `{0,0,1}` would lose that explicit presence across a mirror+undo
    // round-trip without this snapshot). entityId → original
    // `mTextData.extrusion`, or `null` when the source had NO extrusion
    // field at all — same null-sentinel convention as
    // `restoreSecondaryContextExtrusions`, unindexed (one primary context
    // per entity, not an array). Populated ONLY by computeInverse
    // (deleteSource=true, a Multileader whose `contextData.mTextData` is
    // present); absent on every forward (user/AI) dispatch.
    restorePrimaryContextExtrusion: z
      .record(
        z.string(),
        z.object({ x: z.number(), y: z.number(), z: z.number() }).nullable(),
      )
      .optional(),
    // GitHub Codex review PR #558 (formal bot review, HEAD 4fefb166) P2 —
    // "Restore MLeader text directions exactly on undo". Same shape/
    // rationale pair as `restoreSecondaryContextExtrusions`
    // /`restorePrimaryContextExtrusion` immediately above, for
    // `mTextData.textDirection` (DXF group 13/23/33) instead of
    // `.extrusion` — `reflectVectorXY`'s trigonometric reflection is
    // involutive in EXACT arithmetic but NOT bit-exact for a non-axis-
    // aligned mirror axis, so a blind re-reflection on undo would leave a
    // genuine (if tiny) floating-point drift in the re-exported DXF group,
    // independent of the SAME presence-ambiguity every sibling snapshot
    // already resolves.
    restoreSecondaryContextTextDirections: z
      .record(
        z.string(),
        z.record(
          z.string(),
          z.object({ x: z.number(), y: z.number(), z: z.number() }).nullable(),
        ),
      )
      .optional(),
    restorePrimaryContextTextDirection: z
      .record(
        z.string(),
        z.object({ x: z.number(), y: z.number(), z: z.number() }).nullable(),
      )
      .optional(),
    // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — UNDO restore
    // map for a Block entity's per-attribute WORLD `overrideGenerationFlags`
    // (DXF group 71, the handedness companion to `overrideRotation` —
    // `blockInsertAttributeSchema`'s doc comment). Same index-keyed,
    // null-sentinel shape as `restoreAttributeRotations` (the handedness XOR
    // is value-self-inverse for MIRRTEXT=1, but NOT presence-self-inverse —
    // a blind re-XOR of an originally-absent field materializes an explicit
    // `0` instead of returning to absent). Populated ONLY by computeInverse
    // (deleteSource=true, a Block with ≥1 WORLD-placed attribute); absent on
    // every forward (user/AI) dispatch.
    restoreAttributeGenerationFlags: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
    // GitHub Codex review PR #558 P2 — UNDO restore map for a Multileader's
    // OR a Dimension's entity-level `textMirrored` (handedness companion to
    // `textRotation` / `textAngleRelativeToDimLine`; a document-wide entity
    // property, not per-annotative-context — see `multileaderFromEntity.ts`'s
    // doc). entityId → original value, `null` = the source had no
    // `textMirrored` field at all. Populated ONLY by computeInverse
    // (deleteSource=true, every mirrored Multileader/Dimension); absent on
    // every forward (user/AI) dispatch.
    restoreTextMirrored: z.record(z.string(), z.boolean().nullable()).optional(),
  }).refine(
    (cmd) => {
      const dx = cmd.mirrorLine2.x - cmd.mirrorLine1.x;
      const dy = cmd.mirrorLine2.y - cmd.mirrorLine1.y;
      return Math.sqrt(dx * dx + dy * dy) > FLOAT_EPSILON;
    },
    { message: "Mirror axis must be nondegenerate" },
  ),
  z.object({
    type: z.literal("geometry/rotate-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    basePoint: z.object({ x: z.number(), y: z.number() }),
    angleDeg: z
      .number()
      .refine((a) => !Number.isNaN(a) && Number.isFinite(a), {
        message: "Rotation angle must be finite",
      }),
    // MIRRTEXT-DIMENSION-MLEADER-ATTRIB (local Codex read-only review P2,
    // round 4) — UNDO-of-rotate restore map for a Block entity's per-
    // attribute WORLD `overrideRotation` (EATTEDIT absolute rotation
    // override, gated on `overridePosition` being set). Same shape/sentinel
    // convention as `geometry/mirror-entities`' `restoreAttributeRotations`
    // (`null` = the source attribute had NO overrideRotation field at all —
    // undo restores absence, never an emitted `0`). ROTATE's own angle
    // arithmetic (`+angleDeg` then `-angleDeg`) is exactly self-inverse, but
    // `withWorldAttributeRotation`'s presence-preservation rule means an
    // attribute that started GENUINELY ABSENT and gets materialised by the
    // forward rotate cannot, by itself, tell "restore to absent" apart from
    // "restore to an explicit 0" on the blind angle-negation undo — this
    // snapshot resolves the ambiguity. Populated ONLY by computeInverse;
    // absent on every forward (user/AI) dispatch.
    restoreAttributeRotations: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
  }),
  z.object({
    type: z.literal("geometry/scale-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    basePoint: z.object({ x: z.number(), y: z.number() }),
    factor: z
      .number()
      .refine(
        (f) => f !== 0 && Number.isFinite(f) && Math.abs(f) > FLOAT_EPSILON,
        { message: "Scale factor must be nonzero finite" },
      ),
    // GitHub Codex review, PR #558 round 7 P2 — same class of bug as
    // `geometry/rotate-entities`' `restoreAttributeRotations` (round 4/P1):
    // SCALE's own angle-free arithmetic (`factor` then `1 / factor`) is
    // exactly self-inverse, but `scaleEntityRadii`'s presence-preserving
    // `overrideHeight` write-back (round 6 P2, `presencePreservingValue`)
    // means a genuinely-absent WORLD attribute height that a forward SCALE
    // materialises (implicit 2.5 diverges after scaling) cannot, by itself,
    // tell "restore to absent" apart from "restore to an explicit 2.5" on
    // the blind factor-inversion undo — this snapshot resolves the
    // ambiguity. `null` = the source attribute had NO overrideHeight field
    // at all (undo restores absence, never an emitted `2.5` — byte-faithful
    // DXF ATTRIB group-40 round-trip, same null-sentinel convention as
    // `restoreAttributeRotations`). Populated ONLY by computeInverse;
    // absent on every forward (user/AI) dispatch.
    restoreAttributeHeights: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
    // GitHub Codex review PR #558 round 2 (round-14 local read-only
    // follow-up) P2 — "Rotate WORLD attributes for negative scales" +
    // "include its presence-aware value in undo restoration". A negative
    // `factor` now also bumps a WORLD attribute's `overrideRotation` by
    // 180° (`scaleEntityRadii.ts`'s own doc — a single `factor` always
    // negates the Block's scaleX/scaleY TOGETHER, mathematically a 180°
    // turn about the base point). SCALE's own factor arithmetic
    // (`factor` then `1 / factor`, itself always negative-in ⇔
    // negative-out) is self-inverse for the rotation RULE, but
    // `presencePreservingRotation`'s presence ambiguity is the SAME class
    // of bug `restoreAttributeHeights` (just above) already snapshots for
    // — same null-sentinel convention, same undo-only population
    // (computeInverse only; absent on every forward user/AI dispatch).
    restoreAttributeRotations: z
      .record(z.string(), z.record(z.string(), z.number().nullable()))
      .optional(),
    // GitHub Codex review PR #794 round 16 P2 — issue #804. A SCALE now
    // carries an MTEXT's CACHED MEASUREMENT (DXF groups 42/43) with the
    // block it resizes, and CLEARS it where the scaled block is not
    // geometrically similar (an absolute inline `\H` run paints at a world
    // height the scale does not move — `drawing-model::mtextUniformScale-
    // PreservesLayoutShape`). The SCALED case is self-inverse through the
    // same `factor` then `1 / factor` arithmetic `nominalHeight` uses, but
    // the CLEARED case is not invertible at all: the inverse re-dispatch
    // sees 0 and multiplies it by `1 / factor`, so the measurement would
    // leave the document for good and make the edit LOSSY rather than
    // undoable. This snapshots the PRE-scale pair of exactly those
    // entities — entityId → the 42/43 the forward pass is about to drop —
    // the same class of fix as `restoreAttributeHeights` above and the
    // same undo-only population (computeInverse only; absent on every
    // forward user/AI dispatch).
    restoreMtextMeasurements: z
      .record(
        z.string(),
        z.object({
          horizontalWidth: z.number(),
          verticalHeight: z.number(),
        }),
      )
      .optional(),
  }),
  z.object({
    type: z.literal("geometry/offset-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    distance: z
      .number()
      .refine((d) => d > FLOAT_EPSILON, {
        message: "Offset distance must be positive (negative not yet supported for circle/arc)",
      }),
    sidePoint: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/stretch-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
    stretchWindow: z.object({
      x1: z.number(),
      y1: z.number(),
      x2: z.number(),
      y2: z.number(),
    }),
    delta: z.object({ x: z.number(), y: z.number() }),
  }),
  // ── Batch 5: TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN, EXPLODE, LENGTHEN
  z.object({
    type: z.literal("geometry/trim-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    cuttingEdgeIds: z.array(z.string().min(1)).min(1),
    pickPoint: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/extend-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    boundaryEdgeIds: z.array(z.string().min(1)).min(1),
    pickPoint: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/fillet-entities"),
    source: commandSourceSchema,
    entityId1: z.string().min(1),
    entityId2: z.string().min(1),
    radius: z.number().nonnegative(),
    pickPoint1: z.object({ x: z.number(), y: z.number() }),
    pickPoint2: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/chamfer-entities"),
    source: commandSourceSchema,
    entityId1: z.string().min(1),
    entityId2: z.string().min(1),
    dist1: z.number().nonnegative(),
    dist2: z.number().nonnegative(),
    pickPoint1: z.object({ x: z.number(), y: z.number() }),
    pickPoint2: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/break-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    point1: z.object({ x: z.number(), y: z.number() }),
    point2: z.object({ x: z.number(), y: z.number() }),
  }),
  z.object({
    type: z.literal("geometry/join-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(2),
  }),
  z.object({
    type: z.literal("geometry/explode-entities"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal("geometry/lengthen-entity"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    mode: z.enum(["delta", "percent", "total"]),
    value: z.number(),
    endPoint: z.object({ x: z.number(), y: z.number() }),
  }),
  // ── BEDIT / BSAVE / BCLOSE (Phase 2.3 — block edit workflow) ──
  // `space/enter-block-edit` starts a BEDIT session on the named block.
  // When `createIfMissing` is true the block is created on the fly (BEDIT
  // accepts an unknown name as "new block") — otherwise the command fails
  // in the reducer if the block does not exist. The reducer captures a
  // JSON snapshot of the block definition so a subsequent
  // `space/exit-block-edit` with action="discard" can restore it.
  z.object({
    type: z.literal("space/enter-block-edit"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
    createIfMissing: z.boolean().default(false),
  }),
  // `space/exit-block-edit` closes the active BEDIT session. action="save"
  // leaves changes in place (they already live in the BlockDefinition,
  // routed there by the geometry reducer's write-wrapper). action="discard"
  // restores the original BlockDefinition from the snapshot captured on
  // enter. On exit the reducer also reactivates the space the user was in
  // before BEDIT started.
  z.object({
    type: z.literal("space/exit-block-edit"),
    source: commandSourceSchema,
    action: z.enum(["save", "discard"]),
  }),
  // `space/save-block-edit` implements BSAVE (AutoCAD quick-save). The
  // block's current state becomes the new baseline so a later discard
  // throws away only the changes authored *after* the save, not
  // everything since enter. No space change.
  z.object({
    type: z.literal("space/save-block-edit"),
    source: commandSourceSchema,
  }),
  // ── TESTBLOCK / CLOSETESTBLOCK (DYN-1.9) — Test Block sandbox ──
  // `space/enter-block-test` opens a transient, non-persisted sandbox
  // space where the user can preview a dynamic block's parameter /
  // visibility / flip behavior WITHOUT modifying the actual block
  // definition. Typically invoked from the Block Editor to test the
  // block being authored. The sandbox is seeded with a single INSERT
  // at (0,0) of the target block with scale 1 and rotation 0 — the
  // evaluator then runs on every user grip interaction just like on
  // a regular INSERT in the model space.
  //
  // Persistence: the block-test space is never written to DXF (see
  // dxfBridge.ts filter). On session end or CLOSETESTBLOCK it is
  // discarded and the previous active space is restored.
  z.object({
    type: z.literal("space/enter-block-test"),
    source: commandSourceSchema,
    blockName: z.string().min(1),
  }),
  z.object({
    type: z.literal("space/exit-block-test"),
    source: commandSourceSchema,
  }),
  // ── REFEDIT / REFSET / REFCLOSE (Phase FIN.1 — In-Place Block Edit) ──
  // `space/enter-refedit` opens a REFEDIT session targeting the block
  // referenced by `insertId`. The system snapshots the block definition,
  // creates a synthetic refedit space populated with the block children
  // (working set), tints the surrounding host-space entities, and
  // routes geometry mutations to the working-set entities (mirrored
  // back to the block on save).
  z.object({
    type: z.literal("space/enter-refedit"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
  }),
  // `space/exit-refedit` closes the REFEDIT session. action="save" mirrors
  // working-set entities back into the BlockDefinition so all INSERTs of
  // the same block re-render with the new geometry; action="discard"
  // restores the BlockDefinition from the snapshot captured on enter.
  z.object({
    type: z.literal("space/exit-refedit"),
    source: commandSourceSchema,
    action: z.enum(["save", "discard"]),
  }),
  // `space/refedit-add-to-working-set` (REFSET ADD). Adds the listed
  // entity ids to the active REFEDIT session's working set. Entities
  // outside the working set render tinted; once promoted they render
  // with normal opacity and participate in REFCLOSE save.
  z.object({
    type: z.literal("space/refedit-add-to-working-set"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  // `space/refedit-remove-from-working-set` (REFSET REMOVE). Removes
  // the listed entity ids from the working set so their pre-edit form
  // survives REFCLOSE save.
  z.object({
    type: z.literal("space/refedit-remove-from-working-set"),
    source: commandSourceSchema,
    entityIds: z.array(z.string().min(1)).min(1),
  }),
  // ── ANNOUPDATE / OBJECTSCALE / set-cannoscale (Phase FIN.2 — Annotative) ──
  // `header/set-cannoscale` mirrors the AutoCAD CANNOSCALE status-bar
  // selector. The reducer updates the document-level cannoscale +
  // cannoscaleName so annotative entities (INSERTs and other annotative-
  // aware entities) re-render against the new scale on the next pass.
  z.object({
    type: z.literal("header/set-cannoscale"),
    source: commandSourceSchema,
    cannoscaleName: z.string().min(1),
  }),
  // PR-6 — `header/set-cannoscale-and-sync` is the AutoCAD-faithful
  // CANNOSCALE switch. In addition to updating the active cannoscale
  // (sibling `header/set-cannoscale`), it also performs ANNOAUTOSCALE
  // materialization: every annotative entity that lacks a context for
  // the new scale gets one derived from its master fields. Per AutoCAD
  // this is a SINGLE undo step — the reducer mutates the document
  // immutably in one pass via `materializeScaleAcrossDocument` so the
  // history stack records exactly one entry.
  //
  // The status-bar dropdown dispatches THIS command (PR-6 — see
  // AnnotationStatusBar.tsx). The plain `header/set-cannoscale` action
  // is kept as a sibling for callers that explicitly want to switch
  // CANNOSCALE without materializing (raw command-line CANNOSCALE
  // without sync intent, future viewport-specific overrides).
  //
  // - `autoMaterialize` defaults to `true` (matches AutoCAD's default
  //   ANNOAUTOSCALE = 4 behaviour). When `false`, the materialization
  //   pass is skipped regardless of the header's `annoAutoScale` value.
  // - When `annoAutoScale <= 0` (header-level disable) the
  //   materialization is skipped EVEN IF `autoMaterialize` is true —
  //   the header value is the user's persisted "no auto-context"
  //   preference and overrides the per-call request.
  z.object({
    type: z.literal("header/set-cannoscale-and-sync"),
    source: commandSourceSchema,
    cannoscaleName: z.string().min(1),
    autoMaterialize: z.boolean().default(true),
  }),
  // ── BASE / БАЗА ($INSBASE) — drawing insertion base point ──
  // `header/set-insertion-base` mirrors the AutoCAD BASE command. It sets the
  // drawing-level insertion base point — the DXF HEADER `$INSBASE` system
  // variable (group codes 10/20/30 in the HEADER section per the Autodesk DXF
  // reference + ezdxf; NOT 12/22/32, which is the per-layout AcDbLayout
  // insertion base, a different field). `$INSBASE` is the origin AutoCAD uses
  // when this drawing is inserted as a block into another drawing. The reducer
  // writes `doc.insertionBase`; the mutation is invertible via `computeInverse`
  // (which snapshots the prior base from the pre-mutation document) so undo
  // restores the previous point as a single atomic step.
  z.object({
    type: z.literal("header/set-insertion-base"),
    source: commandSourceSchema,
    point: z.object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    }),
  }),
  // ── CECOLOR / COLOR / ЦВЕТ ($CECOLOR) — current-entity-colour sysvar ──
  // `header/set-cecolor` mirrors the AutoCAD COLOR / -COLOR / SETCOLOR / ЦВЕТ
  // command. It sets the drawing-level CURRENT colour — the DXF HEADER `$CECOLOR`
  // system variable (group 62) — that NEWLY-created entities inherit. The colour
  // is the typed `EntityColor` union (ByLayer / ByBlock / index / true-colour /
  // colour-book). The reducer writes `doc.cecolor` (NO entity payload change) and
  // syncs the live `cecolorStore`; the mutation is invertible via `computeInverse`
  // (which snapshots the prior `doc.cecolor`) so undo restores it as one atomic
  // step — the exact `header/set-insertion-base` (BASE) precedent. The CLI /
  // ribbon swatch / AI all route through THIS command (Command-Engine one-truth).
  z.object({
    type: z.literal("header/set-cecolor"),
    source: commandSourceSchema,
    color: entityColorSchema,
  }),
  // `geometry/annoupdate` syncs all annotative INSERTs to the current
  // CANNOSCALE — mirrors AutoCAD's ANNOUPDATE command. The reducer
  // walks every annotative INSERT and rewrites scaleX/scaleY based on
  // the current cannoscale factor.
  z.object({
    type: z.literal("geometry/annoupdate"),
    source: commandSourceSchema,
  }),
  // `geometry/objectscale-add` / `geometry/objectscale-remove` mirror
  // OBJECTSCALE — add or remove an annotation scale context for a
  // specific INSERT. The reducer updates the INSERT's annotationScales
  // list so the renderer's visibility check picks it up.
  z.object({
    type: z.literal("geometry/objectscale-add"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
    scaleName: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/objectscale-remove"),
    source: commandSourceSchema,
    insertId: z.string().min(1),
    scaleName: z.string().min(1),
  }),
  // ── PR-7 — entity/add-annotation-context / entity/remove-annotation-context ──
  // Universal per-entity OBJECTSCALE — works for ANY annotative-capable
  // entity (TEXT/MTEXT/HATCH/INSERT/ATTDEF), not just INSERT (which is
  // covered by the legacy `geometry/objectscale-add/-remove` pair above
  // that only mutates `Block.annotationScales`). Wired by PR-8
  // (OBJECTSCALE command extension) and PR-9 (Properties palette
  // "Annotative Object Scale" dialog).
  //
  // The `context` payload is `z.unknown()` here to keep the contracts
  // package free of drawing-model coupling (`AnnotScaleContext` is a
  // discriminated union over per-entity-kind shapes living in
  // `@dxf-ai-editor/drawing-model/annotative.ts`). The geometry reducer
  // re-validates the full document via `drawingDocumentSchema.parse`
  // after upserting the context, so a malformed payload fails Zod
  // validation at the document level rather than at this contract.
  // Same pattern as `geometry/create-block-definition.entities` (line
  // 715) which also uses `z.array(z.unknown())` for the same reason.
  //
  // Upsert semantics for `add`: if an entry with the same `scaleName`
  // already exists, it is replaced; otherwise the new entry is
  // appended. Idempotent: re-dispatching with an identical context is
  // a no-op (returns the same document reference).
  z.object({
    type: z.literal("entity/add-annotation-context"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    context: z.unknown(),
  }),
  z.object({
    type: z.literal("entity/remove-annotation-context"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    scaleName: z.string().min(1),
  }),
  // ── PR-4 — Annotation status-bar toggles ────────────────────────────
  // These three commands mirror the AutoCAD status-bar buttons next to
  // the CANNOSCALE selector. Each one is a simple inversion of the
  // matching header field — no payload required. The reducer reads the
  // current value and writes the inverse (annoAutoScale uses sign-flip
  // semantics per AutoCAD spec range -4..4).
  z.object({
    type: z.literal("header/toggle-annoallvisible"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("header/toggle-annoautoscale"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("header/toggle-annomonitor"),
    source: commandSourceSchema,
  }),
  // ── PR-9 — SCALELISTEDIT scale-list catalogue mutations ────────────────
  // `header/scalelist-add` appends a new entry to `header.annotationScales`
  // (the document-level catalogue surfaced by AutoCAD's SCALELISTEDIT
  // dialog). Idempotent on duplicate `scaleName` (no-op). The new entry
  // also surfaces in the AnnotationStatusBar dropdown and becomes
  // selectable as the active CANNOSCALE.
  //
  // `header/scalelist-remove` filters the matching entry out of the
  // catalogue. If the removed scale was the current `cannoscaleName`,
  // the reducer resets the active scale to the first remaining entry
  // (or "1:1" when the catalogue becomes empty). Idempotent (no-op when
  // `scaleName` is not present).
  z.object({
    type: z.literal("header/scalelist-add"),
    source: commandSourceSchema,
    scaleName: z.string().min(1),
    scaleFactor: z.number().positive(),
    isHidden: z.boolean().default(false),
  }),
  z.object({
    type: z.literal("header/scalelist-remove"),
    source: commandSourceSchema,
    scaleName: z.string().min(1),
  }),
  // ── Phase 5 — style/layer entity-remap commands ───────────────────────
  // These commands describe atomic mutations to the document-level
  // style tables (dimensionStyles / multileaderStyles / layers) AND the
  // remap of any referencing entity in `spaces[].entities[]`. They are
  // dispatched by editorStore action wrappers (deleteDimensionStyle /
  // renameDimensionStyle / deleteMultileaderStyle / deleteLayer /
  // renameLayer — the last a T7 agent-bridge addition mirroring
  // renameDimensionStyle) so the mutation flows through
  // `reduceGeometryDocument` → `applyStyleTablesActionWithMutations` and
  // emits a `ReducerMutationResult` with `mutations.updated` (entities
  // whose styleName/layer was remapped) + `mutations.styleTablesTouched`.
  //
  // Validation guards (Standard-not-deletable, layer "0" not deletable/
  // renamable, current-layer protection, missing-name-rejection,
  // duplicate-name rejection) live in the editorStore wrapper BEFORE
  // dispatch; the reducer ALSO stays idempotent (silent no-op) on an
  // invalid target so a raw/AI dispatch that bypasses the wrapper (the
  // AI safe contour dispatches EditorCommands directly, never through the
  // wrapper) can never corrupt the table.
  z.object({
    type: z.literal("header/delete-dimension-style"),
    source: commandSourceSchema,
    styleName: z.string().min(1),
  }),
  z.object({
    type: z.literal("header/rename-dimension-style"),
    source: commandSourceSchema,
    oldName: z.string().min(1),
    newName: z.string().min(1),
  }),
  z.object({
    type: z.literal("header/delete-multileader-style"),
    source: commandSourceSchema,
    styleName: z.string().min(1),
  }),
  z.object({
    type: z.literal("header/delete-layer"),
    source: commandSourceSchema,
    layerName: z.string().min(1),
  }),
  // ── T7 (agent-bridge layer-mgmt) — LAYER rename, mirrors
  // `header/rename-dimension-style` exactly (table rename + cascade rename
  // of every referencing entity's `layer` field — the DXF-faithful
  // consequence of a LAYER-table rename, NOT a per-entity CHPROP-style
  // reassignment; see styleTablesReducer.ts's `header/rename-layer` case).
  z.object({
    type: z.literal("header/rename-layer"),
    source: commandSourceSchema,
    oldName: z.string().min(1),
    newName: z.string().min(1),
  }),
  // ── Lane J (D3-1) — style/layer TABLE direct-set → dispatch (undo-able) ──
  // These 8 commands route the formerly-direct-`set` style-table / layout-plot
  // mutators (updateDimensionStyle / addDimensionStyle / updateMultileaderStyle
  // / addMultileaderStyle / updateTextStyle / addLayer / updateLayer /
  // setLayoutPlotSettings) THROUGH the universal dispatch chokepoint. Before
  // Lane J those actions poked `geometryDocument.styleTables` / `.layouts`
  // straight via zustand `set` — bypassing `computeInverse` / history.past — so
  // layer/style/plot edits were NOT undo-able (Ctrl+Z did not see them). Routing
  // them here gives each a history entry + a snapshot undo inverse
  // (`header/restore-style-tables-snapshot`, below).
  //
  // Payloads (`style` / `layer` full records, `updates` partial patches,
  // `settings` plot-settings) are `z.unknown()` / `z.record(...)` — RE-VALIDATED
  // by `assembleDrawingDocument`'s Zod re-parse in `styleTablesReducer.ts` at the
  // mutation boundary — the SAME decoupling the xref (`file/xref-restore-
  // snapshot`) and dynamic-block (`geometry/add-dynamic-parameter`) families use
  // to keep the contract free of the expressive drawing-model record shapes.
  //
  // Validation guards (empty-name / duplicate-name rejection, base-style
  // resolution, imported-handle stripping — SL-4/SL-5) stay in the editorStore
  // action wrappers BEFORE dispatch; the `add*` wrappers construct the FULLY
  // RESOLVED record and pass it here so REDO replays a deterministic record.
  z.object({
    type: z.literal("header/add-dimension-style"),
    source: commandSourceSchema,
    style: z.unknown(),
  }),
  z.object({
    type: z.literal("header/update-dimension-style"),
    source: commandSourceSchema,
    styleName: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("header/add-multileader-style"),
    source: commandSourceSchema,
    style: z.unknown(),
  }),
  z.object({
    type: z.literal("header/update-multileader-style"),
    source: commandSourceSchema,
    styleName: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("header/update-text-style"),
    source: commandSourceSchema,
    styleName: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("header/add-layer"),
    source: commandSourceSchema,
    layer: z.unknown(),
  }),
  z.object({
    type: z.literal("header/update-layer"),
    source: commandSourceSchema,
    layerName: z.string().min(1),
    updates: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("header/set-layout-plot-settings"),
    source: commandSourceSchema,
    spaceId: z.string().min(1),
    settings: z.unknown(),
  }),
  // ── Lane J inverse — history-internal style-table snapshot restore ──────
  // The undo half of EVERY style/layer TABLE + layout-plot command (the 8
  // direct-set commands above AND the four dispatch-routed delete/rename
  // siblings — `header/delete-dimension-style` / `header/rename-dimension-style`
  // / `header/delete-multileader-style` / `header/delete-layer`). Those
  // mutations make document-slice changes (a whole styleTables edit; delete/
  // rename ALSO remap every referencing entity's styleName/layer across the
  // spaces) that no closed-form parametric inverse can reconstruct, so — exactly
  // like `file/xref-restore-snapshot` — the complete inverse is a SNAPSHOT
  // RESTORE of the affected slices captured PRE-mutation. `computeInverse`'s
  // hand-built block (editorStore.ts) snapshots them from `preMutationDoc`; the
  // geometry reducer restores them verbatim so undo is exact and redo re-applies
  // the recorded forward (AutoCAD parity — LAYER / DIMSTYLE / MLEADERSTYLE /
  // PAGESETUP edits are undoable). Only the slices the forward touched are
  // present: `styleTables` for the style/layer edits, `layouts` +
  // `layoutsBySpaceId` for the plot-settings edit, `spaces` ONLY when entities
  // were remapped (delete/rename). Opaque payloads (re-validated by
  // `assembleDrawingDocument` on apply) — same convention as the xref snapshot.
  z.object({
    type: z.literal("header/restore-style-tables-snapshot"),
    source: commandSourceSchema,
    styleTables: z.unknown().optional(),
    layouts: z.record(z.string(), z.unknown()).optional(),
    layoutsBySpaceId: z.record(z.string(), z.unknown()).optional(),
    spaces: z.array(z.unknown()).optional(),
    // T7 (agent-bridge layer-mgmt), local Codex round-2 P2 — `header/rename-
    // layer` also remaps `entity.layer` inside BLOCK-DEFINITION children
    // (`document.blocks[name].entities[]`), not just space entities (a block
    // can legally contain entities on a named layer, and those entities
    // would otherwise keep referencing the OLD name after the table row is
    // renamed away). `document.blocks` is a PLAIN field (not the compact-
    // cache read-through `spaces` is), so a direct pre-mutation reference is
    // already a frozen snapshot — no materialisation needed. Present ONLY
    // when the forward rename actually touched a block-child entity.
    blocks: z.record(z.string(), z.unknown()).optional(),
    // @codex P2 — the CURRENT DIMSTYLE pointer
    // (`defaultObjectProperties["Dimension style"]`, the AutoCAD DIMSTYLE sysvar)
    // is SHELL state, not a document slice, so the snapshot-restore reducer cannot
    // reach it. A dimension-style delete/rename that re-points the current pointer
    // at the dispatch chokepoint carries the PRE-mutation pointer here so UNDO
    // restores it in lockstep with the table (else the pointer would dangle at the
    // now-missing new/deleted name and new dimensions would fall back to the first
    // style). Present ONLY when the forward operation actually moved the pointer
    // (the current style WAS the renamed/deleted one); the shell-resync reconcile
    // in editorStore.ts reads it back. Redo re-applies the forward rename/delete
    // command, which re-runs the same chokepoint reconcile — no field needed there.
    currentDimensionStyle: z.string().optional(),
    // @codex P2 — the value the FORWARD op set the current pointer to (its
    // "automatic value": the renamed-to name, or "Standard" on delete). UNDO
    // restores `currentDimensionStyle` ONLY when the live pointer STILL equals this
    // — so a deliberate `Set Current` to another live style AFTER the table edit (a
    // non-undoable DIMSTYLE-sysvar change) survives the undo instead of being
    // clobbered back to the pre-op value. Carried alongside `currentDimensionStyle`.
    currentDimensionStyleForwardValue: z.string().optional(),
    // T7 (agent-bridge), local Codex round-5 P2 — the CLAYER sibling of
    // `currentDimensionStyle` above. A `header/rename-layer` that renamed
    // the CURRENT layer carries the PRE-rename CLAYER string here so UNDO
    // restores it in lockstep with the table (the forward carries CLAYER to
    // `newName` at the chokepoint reconcile — see `reconcileCurrentLayer
    // AgainstTable`; without this field, undoing the rename would fall
    // CLAYER back to "0" instead of the pre-rename name, since the table no
    // longer carries `newName`). Present ONLY when the forward rename
    // actually moved the pointer.
    currentLayerName: z.string().optional(),
    // T7 (agent-bridge), local Codex round-5 P2 — the value the FORWARD
    // rename set CLAYER to (`newName`). UNDO restores `currentLayerName`
    // ONLY when the live pointer STILL equals this — a deliberate
    // `setCurrentLayer` to a DIFFERENT live layer AFTER the rename (a
    // non-undoable CLAYER-sysvar change) survives the undo instead of being
    // clobbered. Carried alongside `currentLayerName`.
    currentLayerForwardValue: z.string().optional(),
    // T7 (agent-bridge layer-mgmt), local Codex round-10 P2 — undo half of
    // a `header/rename-layer` that remapped the top-level ENTITIES-section
    // RAW (unsupported, verbatim-preserved) siblings' DXF group-8 layer tag
    // (`document.rawEntityGroups[]` — see `remapRawEntityGroupsLayer`,
    // styleTablesReducer.ts). `document.rawEntityGroups` is a PLAIN field
    // (like `blocks` above — no compact-cache read-through), so a direct
    // pre-mutation reference is already a frozen snapshot. Present ONLY
    // when the forward rename actually touched a raw group, mirroring
    // `blocks`'s own reference-identity gating.
    rawEntityGroups: z.array(z.unknown()).optional(),
  }),
  // ── PR-9 — Shell-only dialog-open actions ─────────────────────────────
  // These two actions are SHELL events (no geometry mutation). They are
  // dispatched by:
  //   - the AnnotationStatusBar SCALELISTEDIT button (open-scalelist-dialog)
  //   - the SCALELISTEDIT command descriptor (open-scalelist-dialog) so
  //     the command-line surface routes through the same audit pipeline
  //   - the Properties palette "Annotative Object Scale" "..." button
  //     (open-annotative-object-scale-dialog)
  //
  // The dialog state itself lives in App.tsx local React state — these
  // actions only emit an audit event so the dispatch leaves a trail.
  // Routing: handled in `shellUiReducer.ts`, NOT in the geometry reducer.
  // The corresponding command descriptors that claim these literals live
  // in `command-registry/src/commands/system.ts` (SCALELISTEDIT) and
  // `coverage-allowlist.ts` (annotative-object-scale — no CLI surface).
  z.object({
    type: z.literal("system/open-scalelist-dialog"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("system/open-annotative-object-scale-dialog"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
  }),
  // ── PURGE (Phase 2.7) — remove unreferenced block definitions ──
  // The UI opens PurgeBlocksDialog, runs `purgeBlocksMutation` client-side
  // to populate the candidate list, then dispatches this command with the
  // user-confirmed subset. The reducer re-runs analysis defensively so
  // command-line bypasses (future -PURGE) can never remove a block that
  // has gained references since the dialog was opened.
  z.object({
    type: z.literal("file/purge-blocks"),
    source: commandSourceSchema,
    blockNames: z.array(z.string().min(1)).min(1),
  }),
  // ── RENAME (Phase 2.8) — rename a block with cascade across INSERTs ──
  // The UI opens RenameBlockDialog with validation inline (duplicate /
  // reserved / forbidden chars). On OK the reducer calls renameBlockMutation
  // which rewrites blocks[newName], blockRecords[newName], and every
  // INSERT reference in both spaces and nested block definitions.
  z.object({
    type: z.literal("file/rename-block"),
    source: commandSourceSchema,
    oldName: z.string().min(1),
    newName: z.string().min(1),
  }),
  // ── XREF suite (Phase FIN.3) — XATTACH / XBIND / XUNLOAD / XRELOAD /
  //    XDETACH / XCLIP. Each command targets an xref by block name. The
  //    UI opens XrefManagerDialog (XREF / -XREF) for centralised
  //    management; the individual commands operate on a single xref
  //    selected from there or typed at the command line.
  //
  // XATTACH carries pre-parsed entities because the browser cannot read
  // arbitrary files synchronously — the file picker happens in App.tsx,
  // the parser runs there, then the entities are dispatched here.
  z.object({
    type: z.literal("file/xattach"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
    xrefPath: z.string(),
    xrefEntities: z.array(z.unknown()).default([]),
    attachmentType: z.enum(["attach", "overlay"]),
    insertionPoint: z.object({ x: z.number(), y: z.number() }),
    insertSpaceId: z.string().min(1),
  }),
  z.object({
    type: z.literal("file/xbind"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
  }),
  z.object({
    type: z.literal("file/xunload"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
  }),
  z.object({
    type: z.literal("file/xreload"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
    /** Optional fresh entities (re-parsed external file). */
    newEntities: z.array(z.unknown()).optional(),
    /** Optional new path when the user re-points the xref. */
    newPath: z.string().optional(),
  }),
  z.object({
    type: z.literal("file/xdetach"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
  }),
  z.object({
    type: z.literal("file/xclip"),
    source: commandSourceSchema,
    xrefName: z.string().min(1),
    /**
     * Boundary to apply. `null` clears any existing boundary
     * (XCLIP DELETE in AutoCAD).
     */
    boundary: z
      .object({
        type: z.enum(["rectangle", "polygon"]),
        vertices: z.array(z.object({ x: z.number(), y: z.number() })).min(2),
      })
      .nullable(),
  }),
  // ── XREF history inverse (ABB-S1 round-3 F2/F3) ──────────────────────────
  // The history-internal inverse of EVERY `file/x*` command. The xref
  // mutations (xattach/xbind/xunload/xreload/xdetach/xclip) make complex
  // multi-field changes — adding/removing INSERTs across every space AND
  // nested block definition (XDETACH), flipping block flags + dropping the
  // registry entry (XBIND), editing the registry clip/status (XCLIP/XUNLOAD).
  // A precise per-command inverse for XDETACH/XBIND would have to reconstruct
  // entities removed across the whole document, so the correct, complete
  // inverse is a SNAPSHOT RESTORE of the four xref-bearing document slices
  // captured PRE-mutation. `computeInverse` (editorStore) snapshots them from
  // `preMutationDoc`; the geometry reducer restores them verbatim so undo is
  // exact and redo re-applies (AutoCAD parity — XATTACH/XDETACH are undoable).
  // The slices are passed as opaque arrays/records (re-validated by
  // `assembleDrawingDocument` on apply) to avoid coupling the contract to the
  // full BlockDefinition/BlockRecord/space shapes.
  z.object({
    type: z.literal("file/xref-restore-snapshot"),
    source: commandSourceSchema,
    /** Pre-mutation `blocks` dict (block-name → BlockDefinition). */
    blocks: z.record(z.string(), z.unknown()),
    /** Pre-mutation `blockRecords` dict (block-name → BlockRecord). */
    blockRecords: z.record(z.string(), z.unknown()),
    /** Pre-mutation `xrefRegistry` dict (block-name → XrefRegistryEntry). */
    xrefRegistry: z.record(z.string(), z.unknown()),
    /** Pre-mutation `spaces` array (restores INSERTs removed by XDETACH). */
    spaces: z.array(z.unknown()),
  }),
  // ── Phase MC5 — per-viewport override commands ───────────────────────────
  // These five commands mirror AutoCAD's VPLAYER / VPCLIP / MVSETUP and
  // the paper-space "Display Locked" toggle. Each targets a single viewport
  // by id and updates the viewport entity's XDATA round-trip data AND the
  // in-memory entity fields.  All route through `reduceGeometryDocument`
  // via `applyLayoutAction` (geometryReducerLayout.ts), which already owns
  // `geometry/create-viewport`.
  //
  // geometry/viewport-set-lock — toggle displayLocked (AutoCAD lock/unlock VP).
  z.object({
    type: z.literal("geometry/viewport-set-lock"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    locked: z.boolean(),
  }),
  // geometry/viewport-set-shadeplot — set the plot-time shade mode (DXF group
  // 170). PLOT-TIME-ONLY: affects how the VP renders when PLOTTED, never the
  // on-screen display, so there is no render-path change. Base AutoCAD modes
  // 0=As Displayed, 1=Wireframe, 2=Hidden, 3=Rendered.
  //
  // Range: capped 0..3 — the four base authorable modes. Modes 4 (Visual Style)
  // / 5 (Render Preset) are NOT authorable through this command: they reference
  // a named visual-style / render-preset object handle (DXF 348 / 333) the data
  // model does NOT yet carry, so authoring a bare 4/5 would write a dangling
  // mode. They are IMPORT-ONLY — an imported file's raw 4/5 round-trips verbatim
  // through the parse → compact-store → export path (read-only, never re-authored
  // here) pending the deferred 348/333 visual-style subsystem
  // (docs/test-gaps/viewport-shadeplot-visualstyle-modes.md).
  //
  // A viewport CURRENTLY in mode 4/5 is by-construction READ-ONLY for shade plot:
  // `applyViewportSetShadePlot` no-ops on it (viewportCommands.ts), so editing it
  // never enters history and no undo inverse carrying 4/5 is ever built — the
  // capped schema therefore never has to accept a 4/5 inverse. Non-UI /
  // programmatic callers cannot author a dangling 4/5 because this schema rejects
  // anything > 3, and the only path that ever sets 4/5 is import (which bypasses
  // commands). This command is also NOT aiCallable (absent from command-registry).
  //
  // `shadePlotMode` is OPTIONAL (.optional()): the ViewportInspector dropdown (the
  // canonical editor — shade plot is display-only in the grid / MATCHPROP, not
  // copyable) always sends a concrete 0..3, and authoring stays capped at 0..3.
  // `undefined`
  // is RESERVED for the undo inverse to RESTORE an absent DXF group-170 record:
  // a viewport that had NO group 170 (model `shadePlotMode === undefined`) that is
  // edited then undone must return to ABSENCE, not 0. If the inverse collapsed
  // undefined→0 a later save would gain a spurious `170=0` (the writer emits group
  // 170 whenever `shadePlotMode !== undefined`, writer.ts ~1104). Absence is ONLY
  // ever produced by the inverse — never authored by the UI / a forward command.
  z.object({
    type: z.literal("geometry/viewport-set-shadeplot"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    shadePlotMode: z.number().int().min(0).max(3).optional(),
  }),
  // geometry/viewport-vplayer — freeze or thaw a named layer in the VP.
  z.object({
    type: z.literal("geometry/viewport-vplayer"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    action: z.enum(["freeze", "thaw"]),
    layerName: z.string().min(1),
    // UNDO-restore of the pre-mutation NATIVE group-331 handle carrier
    // (`frozenLayerHandles`). A forward thaw drops the thawed layer's native
    // handle POSITIVELY (applyViewportVplayer / EXPORT-VP-FROZEN); the plain
    // freeze inverse does NOT re-add a symmetric handle, so undoing a thaw would
    // otherwise re-derive the handle from the layer table and APPEND it, losing
    // the source 331 ORDER/CASING for an imported viewport (@codex #486). The
    // inverse therefore snapshots the prior list and the forward restores it
    // VERBATIM — preserve-source-never-infer, mirroring the set-view inverse:
    //   • string[] → restore that exact 331 list,
    //   • null     → the pre-mutation viewport had NO native carrier → absence,
    //   • omitted  → NORMAL forward (user/AI) — the positive thaw-drop runs.
    restoreFrozenLayerHandles: z.array(z.string()).nullable().optional(),
  }),
  // geometry/viewport-clip-set — set a rectangular clip boundary (VPCLIP).
  z.object({
    type: z.literal("geometry/viewport-clip-set"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    bounds: z.object({
      x1: z.number(),
      y1: z.number(),
      x2: z.number(),
      y2: z.number(),
    }),
  }),
  // geometry/viewport-clip-set-polygon — author a NON-rectangular (polygonal)
  // clip boundary on a viewport (VPCLIP "Polygonal" / "Select clipping object").
  //
  // AutoCAD parity: VPCLIP lets the user either draw a polygon on the sheet
  // (Polygonal) OR pick an existing closed boundary object (a closed LWPOLYLINE
  // etc.). Both produce a PAPER-space clip polygon that the viewport clips its
  // model window to. The producer authors a TYPED clip boundary
  // (`clipBoundaryPaperSpace` paper-space polygon + the derived model-space
  // `clipPath` the renderer's stencil pass consumes) — a polygon of points, NOT
  // a boolean flag (ПРИОРИТЕТ #1). It supersedes any prior imported clip
  // (SPATIAL_FILTER 340 handle / native-340 boundary / group-90 non-rect bit).
  //
  // The boundary source is EXACTLY ONE of:
  //   • `boundary` — an explicit ≥3-vertex PAPER-space polygon (Polygonal /
  //     scripted points), OR
  //   • `boundaryEntityId` — the id of a closed boundary ENTITY (Select
  //     clipping object) the producer resolves to its polygon against the
  //     document (a closed LWPOLYLINE, incl. bulge tessellation — curved
  //     CIRCLE/ELLIPSE/SPLINE/REGION boundaries are the deferred
  //     S-VIEWPORT-CLIP-340-CURVED slice and no-op here).
  // Both fields are `.optional()` here because a discriminated-union member must
  // stay a plain ZodObject (a `.superRefine` would wrap it in a ZodEffects that
  // z.discriminatedUnion rejects). A user/AI/scripted dispatch supplies EXACTLY
  // ONE; the producer (`applyViewportClipSetPolygon`) enforces it: a `boundary`
  // wins for the POLYGON when present, a `boundaryEntityId` supplies the object's
  // 340 export handle (@codex #496 P1), and neither / an unresolvable object is a
  // mutated:false no-op (never a corrupt clip). The history REDO-freeze
  // (editorStore) records BOTH on purpose — the frozen `boundary` for a
  // deterministic redo polygon PLUS the `boundaryEntityId` so redo re-records the
  // export handle — which the producer handles by the same two-field split.
  z.object({
    type: z.literal("geometry/viewport-clip-set-polygon"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    boundary: z
      .array(z.object({ x: z.number(), y: z.number() }))
      .min(3)
      .optional(),
    boundaryEntityId: z.string().min(1).optional(),
  }),
  // geometry/viewport-clip-remove — remove clip boundary (VPCLIP Delete).
  z.object({
    type: z.literal("geometry/viewport-clip-remove"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
  }),
  // geometry/viewport-set-scale — set VP drawing scale (MVSETUP scale).
  // scaleNum / scaleDenom express the ratio as num:denom (e.g. 1:50).
  z.object({
    type: z.literal("geometry/viewport-set-scale"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    scaleNum: z.number().positive(),
    scaleDenom: z.number().positive(),
  }),
  // geometry/viewport-set-view — persist VP pan/zoom view state (MC4).
  // viewCenter (DXF group 12/22, pan) and viewHeight (group 45, zoom
  // magnification in MODEL units) are FIRST-CLASS entity fields, not XDATA.
  // Both are optional so pan-only (viewCenter) and zoom-only (viewHeight)
  // updates can be expressed independently. Setting viewHeight re-derives
  // vpOverrideScale so the status-bar scale selector reflects "Custom" when
  // the magnification falls off a named preset.
  z.object({
    type: z.literal("geometry/viewport-set-view"),
    source: commandSourceSchema,
    viewportId: z.string().min(1),
    viewCenter: z.object({ x: z.number(), y: z.number() }).optional(),
    viewHeight: z.number().positive().optional(),
    // UNDO-restore of the pre-pan/zoom displayed scale (preserve-source).
    // Present ONLY on the computed inverse — a forward (user/AI) dispatch OMITS
    // it. Setting viewHeight makes the forward re-derive
    // `vpOverrideScale = {1, round(viewHeight/height)}` LOSSILY, which destroys
    // a custom rational (e.g. a 2:3 from MVSETUP/applyViewportSetScale, or any
    // non-integer ratio) on undo of a pan — the inverse replays the pre-mutation
    // viewHeight, re-triggering the round.
    //
    // The displayed scale has TWO INDEPENDENT storage carriers that can DIVERGE
    // (applyViewportSetLock / applyViewportVplayer persist the XDATA scale
    // WITHOUT the typed field), so the inverse snapshots BOTH and the forward
    // restores EACH exactly — never inferring one from the other:
    //   • `restoreVpOverrideScale` = the typed `vpOverrideScale` entity field,
    //   • `restoreXdataScale`      = the EDIT_DWG_VP_OVERRIDES XDATA scale codes.
    // For `restoreVpOverrideScale` the forward SKIPS the re-derive and:
    //   • {num,denom} → restore that exact rational on the typed field,
    //   • null        → the viewport had NO typed override → restore ABSENCE,
    //   • omitted     → NORMAL forward: current re-derive behaviour, unchanged.
    // Shape mirrors the model `vpOverrideScale` (plain z.number, no positivity
    // cap) and the shade-plot inverse's verbatim `prior` snapshot.
    restoreVpOverrideScale: z
      .object({ num: z.number(), denom: z.number() })
      .nullable()
      .optional(),
    // UNDO-restore of the pre-mutation VP_OVERRIDES XDATA scale (the DXF-export
    // carrier of the displayed scale), snapshotted INDEPENDENTLY of the typed
    // field so a scale an earlier lock/freeze edit persisted survives undo of an
    // unrelated pan/zoom (@codex #419). Present ONLY on the computed inverse:
    //   • {num,denom} → the pre-mutation XDATA carried this scale → re-write it,
    //   • null        → the pre-mutation XDATA had NO scale → strip the codes,
    //   • omitted     → NORMAL forward (re-derive unchanged); also unreachable on
    //                   the undo path since the inverse always co-emits it.
    restoreXdataScale: z
      .object({ num: z.number(), denom: z.number() })
      .nullable()
      .optional(),
  }),
  z.object({
    type: z.literal("commandline/submit"),
    source: commandSourceSchema,
    text: z.string().min(1),
    /**
     * Bare-command-entry audit-tone fix — an OPTIONAL pre-resolved outcome
     * tone for a dispatch whose `text` is an ALREADY-DETERMINED status
     * message, not raw command-line input for `routeLayoutCommand`
     * (editorStore.ts) to attempt-parse as a scripted geometry command.
     *
     * `useDraftSession.beginDrafting` (apps/web) is the primary emitter: the
     * REAL interactive command line (typing "LINE"/"CIRCLE"/… + Enter) starts
     * a draft session AND separately logs a "<command> armed in <space>."
     * confirmation into the audit trail via this SAME `commandline/submit`
     * action type. Before this field existed, that confirmation had no way
     * to opt out of `routeLayoutCommand`'s generic LINE/POINT/CIRCLE/ARC text
     * resolver, which reparsed e.g. "LINE armed in Model." as malformed LINE
     * coordinate arguments and stamped an `error`-tone audit row — even
     * though the command legitimately started (AutoCAD parity: typing a bare
     * command name starts the command and prompts for input, it is never a
     * rejection). When PRESENT, `routeLayoutCommand` records `text` VERBATIM
     * with this tone and returns immediately (no resolution attempt at all).
     * When ABSENT (the default), `text` is treated as raw command-line
     * input and routed through the full resolution pipeline unchanged — this
     * preserves the genuinely-malformed-scripted-submission error path
     * (e.g. a raw `commandline/submit` of bare "LINE" with no session behind
     * it, which cannot "wait" for a follow-up and is correctly rejected).
     */
    resolvedTone: telemetryToneSchema.optional(),
  }),
  // ── Phase 4.1.a — Undo / Redo foundation ──────────────────────
  // The history slice + dispatcher wrapping landed in 4.1.a; the
  // per-command inverse contracts (4.1.c onward) extend the
  // mutation return shape. These five command types route through
  // the dispatch wrapper that owns the past/future stacks.
  z.object({
    type: z.literal("history/undo"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("history/redo"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("history/begin-batch"),
    source: commandSourceSchema,
    /** User-facing label shown next to the Undo button tooltip. */
    label: z.string().min(1),
  }),
  z.object({
    type: z.literal("history/commit-batch"),
    source: commandSourceSchema,
  }),
  z.object({
    type: z.literal("history/abort-batch"),
    source: commandSourceSchema,
  }),
  /**
   * Phase 4.1.f — synthetic replay commands produced by commit-batch.
   *
   * `history/replay-forwards` replays each command in `batch` in order
   * (used as the `forward` of a batched HistoryEntry — i.e. redo).
   * `history/replay-inverses` replays each command in `batch` in order,
   * where `batch` is already reversed (used as the `inverse` — i.e. undo).
   *
   * `batch` is `z.array(z.unknown())` to avoid a Zod circular dependency
   * (same reason as HistoryEntry.forward/inverse). At runtime the
   * dispatcher casts each element back to EditorCommand.
   *
   * `siblingBatch` (P1, post-review round 6, COLLAB-LOCK-COMMAND finding
   * #15) — the OTHER half of the SAME `HistoryEntry` (`replay-forwards`
   * carries the entry's `inverses`; `replay-inverses` carries its
   * `forwards`), threaded on by `history/commit-batch`. Used ONLY to roll
   * back a partially-applied batch when the fail-closed collab-lock veto
   * stops the replay loop partway through: each already-applied sub-command
   * gets undone via its OWN known counterpart from `siblingBatch` (index
   * math in `editorStore.ts`'s two `history/replay-*` handlers), never a
   * predicted/dry-run inverse — see those handlers' doc comments for why a
   * prediction is unsafe for un-stamped CREATE commands. Optional because
   * only a BATCHED entry's replay commands carry one; a compound single-
   * command inverse `computeInverse` hand-builds (e.g. delete + dangling-
   * viewport-clip-restore) has no natural sibling and is left un-rollback-
   * able on a mid-compound veto — a narrow, pre-existing, documented gap
   * (see the handlers), not a new regression.
   */
  z.object({
    type: z.literal("history/replay-forwards"),
    source: commandSourceSchema,
    batch: z.array(z.unknown()),
    siblingBatch: z.array(z.unknown()).optional(),
  }),
  z.object({
    type: z.literal("history/replay-inverses"),
    source: commandSourceSchema,
    batch: z.array(z.unknown()),
    siblingBatch: z.array(z.unknown()).optional(),
  }),
  // ── COLLAB-LOCK-COMMAND — LOCK / UNLOCK soft-lock CLI commands ──────────
  // AutoCAD-adjacent (no direct AutoCAD analogue — collaboration is a
  // browser-native extension): LOCK / LOCK LAYER <name> soft-locks a
  // selected entity or a named layer so OTHER real-time collaborators
  // cannot edit it; UNLOCK / UNLOCK LAYER <name> releases it. Dispatched by
  // `routeLayoutCommand` (editorStore.ts) — reduced by `shellUiReducer.ts`
  // to an AUDIT-ONLY acknowledgement (NO_MUTATIONS); the actual async
  // `collabLockStore` request/release call + its eventual grant/deny
  // report happen as a side effect in the SAME CLI-intercept block, not in
  // this reducer. `scopes` mirrors the wire `LockScope[]` shape 1:1
  // (`collabLockTypes.ts` / `collabTypes.ts`).
  z.object({
    type: z.literal("collab/lock-request"),
    source: commandSourceSchema,
    scopes: z
      .array(z.object({ kind: z.enum(["entity", "layer"]), targetId: z.string().min(1) }))
      .min(1),
  }),
  z.object({
    type: z.literal("collab/lock-release"),
    source: commandSourceSchema,
    scopes: z
      .array(z.object({ kind: z.enum(["entity", "layer"]), targetId: z.string().min(1) }))
      .min(1),
  }),
  // LOCK LIST / LOCK ? — a read-only query of the live `collabLockStore`
  // snapshot (no network round trip; NOT connected → a real "not connected"
  // audit message, never a silent no-op). Reduced identically to the two
  // above (audit-only acknowledgement); the actual listing is formatted as
  // the async-outcome-reporting side effect.
  z.object({
    type: z.literal("collab/list-locks"),
    source: commandSourceSchema,
  }),
]);

// ── DYNLIST / DYNSET / DYNRESET — INSERT customProperties CLI ──
// AutoCAD-style command-line access to the flat
// `customProperties: Record<string, string | number | boolean>` bag
// the DXF bridge populates from each dynamic-block master's authored
// parameter defaults (see `packages/drawing-model/src/dxfBridge.dynamic.params.ts`).
//
// All three operate on a single INSERT identified by `entityId` (the
// caller resolves the target from the current selection). The shell
// reducer logs the transcript summary via pushAuditEvent; the document
// reducer mutates `customProperties` on the target entity. No geometry
// re-evaluation happens — this is storage-only state editing, matching
// the 8ab89de commit scope note.
//
// • DYNLIST  — dumps the selected INSERT's customProperties + inferred
//   types + (when available from block.dynamicMetadata.parameters) the
//   typed metadata (choices for visibility/lookup, default for all).
// • DYNSET   — updates one customProperty. Value is parsed against the
//   current value's type (number / boolean / string with optional
//   choices). Unknown names or type mismatches emit a warning summary.
// • DYNRESET — re-seeds customProperties from the master block's
//   authored defaults (calls attachInsertCustomProperties for the
//   single INSERT).
//
// Authored as a separate discriminatedUnion + z.union to keep the main
// editorCommandSchema below the ~109-variant threshold beyond which
// TypeScript degrades discriminated-union type inference inside the
// zustand store creator (manifests as `any`-widening across
// useEditorStore callbacks). Narrowing via `command.type === "dynamic/…"`
// still works on consumers.
export const dynamicBlockCommandSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("dynamic/list"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
  }),
  z.object({
    type: z.literal("dynamic/set"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    paramName: z.string().min(1),
    rawValue: z.string(),
  }),
  // Phase N.4 — per-INSERT representation override commands. These mark
  // the override on the typed `representationOverrides.parameterOverrides`
  // map (so the writer emits AcDbBlockRepresentationData on save) AND
  // sync the runtime `customProperties` bag (so the evaluator picks up
  // the divergence immediately). `clear` removes the typed override
  // entry; the runtime value reverts to the BlockDefinition default.
  z.object({
    type: z.literal("geometry/set-insert-parameter-override"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    paramName: z.string().min(1),
    rawValue: z.string(),
  }),
  z.object({
    type: z.literal("geometry/clear-insert-parameter-override"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    paramName: z.string().min(1),
  }),
  z.object({
    type: z.literal("geometry/set-insert-visibility-state"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
    /** Visibility state id (or empty string to clear the override). */
    stateId: z.string(),
  }),
  z.object({
    type: z.literal("dynamic/reset"),
    source: commandSourceSchema,
    entityId: z.string().min(1),
  }),
]);

export const fullEditorCommandSchema = z.union([
  editorCommandSchema,
  dynamicBlockCommandSchema,
]);

export type UserRole = z.infer<typeof userRoleSchema>;
export type AccessStatus = z.infer<typeof accessStatusSchema>;
export type CommandSource = z.infer<typeof commandSourceSchema>;
export type MatchpropMode = z.infer<typeof matchpropModeSchema>;
export type PanelId = z.infer<typeof panelSchema>;
export type LayoutContext = z.infer<typeof layoutContextSchema>;
export type SpaceKind = z.infer<typeof spaceKindSchema>;
export type EntityType = z.infer<typeof entityTypeSchema>;
export type SessionUser = z.infer<typeof sessionUserSchema>;
export type ViewportSnapshot = z.infer<typeof viewportSnapshotSchema>;
export type DrawingSpace = z.infer<typeof drawingSpaceSchema>;
export type EntityPreview = z.infer<typeof entityPreviewSchema>;
export type PropertyValueMap = z.infer<typeof propertyValueMapSchema>;
export type TranscriptEntry = z.infer<typeof transcriptEntrySchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;
export type TelemetryEventKind = z.infer<typeof telemetryEventKindSchema>;
export type TelemetryNavKind = z.infer<typeof telemetryNavKindSchema>;
export type TelemetryTone = z.infer<typeof telemetryToneSchema>;
export type TelemetryStatus = z.infer<typeof telemetryStatusSchema>;
export type TelemetryParams = z.infer<typeof telemetryParamsSchema>;
/** In-memory audit-ring payload shape (discriminants required). See `auditTaxonomy.ts` for the typed `AuditOperationParams` union it stores. */
export type AuditRingParams = z.infer<typeof auditOperationParamsSchema>;
export type TelemetryCommandEvent = z.infer<typeof telemetryCommandEventSchema>;
export type TelemetryViewOpEvent = z.infer<typeof telemetryViewOpEventSchema>;
export type TelemetryUiClickEvent = z.infer<typeof telemetryUiClickEventSchema>;
export type TelemetryUiToggleEvent = z.infer<
  typeof telemetryUiToggleEventSchema
>;
export type TelemetryUiNavEvent = z.infer<typeof telemetryUiNavEventSchema>;
export type TelemetryEvent = z.infer<typeof telemetryEventSchema>;
export type TelemetryBatch = z.infer<typeof telemetryBatchSchema>;
export type TextStyleEntry = z.infer<typeof textStyleEntrySchema>;
export type DimensionStyleEntry = z.infer<typeof dimensionStyleEntrySchema>;
export type MultileaderStyleEntry = z.infer<typeof multileaderStyleEntrySchema>;
export type StyleTablesShell = z.infer<typeof styleTablesShellSchema>;
export type EditorShellState = z.infer<typeof editorShellStateSchema>;
export type DynamicBlockCommand = z.infer<typeof dynamicBlockCommandSchema>;
export type EditorCommand = z.infer<typeof editorCommandSchema>;
/** EditorCommand widened with the DYN CLI variants (DYNLIST/DYNSET/DYNRESET). */
export type EditorCommandWithDyn = EditorCommand | DynamicBlockCommand;
